import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck, Megaphone, Play, Pause, Square, Compass, ExternalLink, Menu as MenuIcon, X as CloseIcon, Inbox as InboxIcon, Reply, RefreshCw, ListChecks, Upload, Undo2, Send, Mail, Users, FileText, Settings as SettingsIcon, History, LayoutDashboard, Plus, Search, 
  CheckCircle2, AlertTriangle, ArrowLeft, Pencil, Trash2, Ban, LogOut, Clock,
} from 'lucide-react';
import { authFetch, API } from '../api';
import { useAuth } from '../AuthContext';

/* ─────────────────────────────────────────────────────────────────────────
   Recruiting Center

   An athlete's private workspace for writing to college coaches. Messages are
   drafted, reviewed and approved here; nothing is emailed from this screen.
   Used by the athlete for themselves and by the platform administrator.
   ───────────────────────────────────────────────────────────────────────── */

const input = 'w-full bg-[#0A1628] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/40 ' +
  'focus:border-[#00D4FF] focus:outline-none text-sm';
const label = 'block text-[#B0BEC5] text-xs font-semibold mb-1.5';
const btn = 'inline-flex items-center justify-center gap-1.5 rounded-lg px-3.5 py-2.5 text-sm font-semibold ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';
const btnPrimary = `${btn} bg-[#00D4FF] text-[#0A0E27]`;
const btnGhost = `${btn} border border-[#546E7A]/50 text-[#B0BEC5]`;
const btnDanger = `${btn} border border-[#EF5350]/50 text-[#EF5350]`;
const card = 'rounded-xl border border-[#546E7A]/25 bg-[#1A2744] p-4';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// Imported dates were read as Central Time, so they are always shown in Central
// Time, whatever the viewer's own clock says.
const CENTRAL = { timeZone: 'America/Chicago' };
const fmtCentralDate = (v) => new Date(v).toLocaleDateString(undefined, { ...CENTRAL, month: 'short', day: 'numeric', year: 'numeric' });
const fmtCentralDateTime = (v) => `${new Date(v).toLocaleString(undefined, { ...CENTRAL, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} Central`;
// An imported date may be a full date and time, a date only, or not known at all.
const fmtImported = (at, precision) => (precision === 'datetime' ? fmtCentralDateTime(at) : fmtCentralDate(at));
const fmtSent = (e) => {
  if (!e.sent_at) return e.sent_original ? `date not understood (“${e.sent_original}”)` : 'date unknown';
  return fmtImported(e.sent_at, e.sent_precision);
};
const unknown = <span className="text-[#546E7A] italic">Unknown</span>;

// One vocabulary for a message's state, used everywhere it appears.
const STATUS = {
  draft: { text: 'Draft · not sent', cls: 'text-[#FFB300] bg-[#FFB300]/12 border-[#FFB300]/35' },
  approved: { text: 'Ready · not sent', cls: 'text-[#00D4FF] bg-[#00D4FF]/12 border-[#00D4FF]/35' },
  sending: { text: 'Sending…', cls: 'text-[#FFB300] bg-[#FFB300]/12 border-[#FFB300]/35' },
  uncertain: { text: 'Unknown · needs checking', cls: 'text-[#EF5350] bg-[#EF5350]/12 border-[#EF5350]/35' },
  sent: { text: 'Sent', cls: 'text-[#00D4FF] bg-[#00D4FF]/12 border-[#00D4FF]/35' },
  replied: { text: 'Replied', cls: 'text-[#00E676] bg-[#00E676]/12 border-[#00E676]/35' },
  delivery_failed: { text: 'Failed', cls: 'text-[#EF5350] bg-[#EF5350]/12 border-[#EF5350]/35' },
  // Sent before the Recruiting Center and imported: to the reader it is simply a sent message.
  imported: { text: 'Sent', cls: 'text-[#00D4FF] bg-[#00D4FF]/12 border-[#00D4FF]/35' },
  imported_unverified: { text: 'Imported · sending not confirmed', cls: 'text-[#B0BEC5] bg-[#546E7A]/15 border-[#546E7A]/50' },
  cancelled: { text: 'Discarded', cls: 'text-[#546E7A] bg-[#546E7A]/12 border-[#546E7A]/35' },
  failed: { text: 'Failed', cls: 'text-[#EF5350] bg-[#EF5350]/12 border-[#EF5350]/35' },
};
// Sending is not delivery: a sent message is shown as replied or failed only when mail came back saying so.
const StatusPill = ({ status, outcome }) => {
  const key = outcome === 'replied' ? 'replied' : outcome === 'failed' ? 'delivery_failed' : status;
  const s = STATUS[key] || { text: status, cls: 'text-[#B0BEC5] border-[#546E7A]/35' };
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 whitespace-nowrap ${s.cls}`}>{s.text}</span>;
};

const ErrorNote = ({ children }) => (children ? (
  <div className="text-sm text-[#EF5350] bg-[#EF5350]/10 border border-[#EF5350]/30 rounded-lg px-3 py-2">{children}</div>
) : null);

/* The API scopes every call to one athlete. An athlete is always themselves;
   the administrator names the athlete on each request. */
const useApi = (playerId, isAdmin) => useCallback(async (path, options = {}) => {
  const url = new URL(`${API}/api/recruiting${path}`, window.location.origin);
  if (isAdmin && playerId) url.searchParams.set('player_id', playerId);
  const res = await authFetch(url.toString(), options.body
    ? { ...options, body: JSON.stringify(options.body) } : options);
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const d = data?.detail;
    const err = new Error(typeof d === 'string' ? d : d?.message || (Array.isArray(d) ? d[0]?.msg : null) || `Request failed (${res.status})`);
    err.status = res.status;
    err.detail = d;
    throw err;
  }
  return data;
}, [playerId, isAdmin]);

/* ── Dashboard ───────────────────────────────────────────────────────────── */

const Dashboard = ({ api, go, openEmail }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api('/dashboard').then(async (dash) => {
      let totals = null;
      try { totals = (await api('/tracking')).totals; } catch { /* tiles are simply left out */ }
      setD({ ...dash, tracking: totals });
    }).catch((e) => setError(e.message));
  }, [api]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!d) return <div className="text-[#546E7A] text-sm">Loading…</div>;
  const c = d.counts;
  const tiles = [
    ...(d.tracking ? [
      { n: d.tracking.new_replies, t: 'New replies', s: 'not yet read', tab: 'inbox', hot: d.tracking.new_replies > 0 },
      { n: d.tracking.colleges_replied, t: 'Colleges replied', s: `of ${d.tracking.colleges_contacted} contacted`, tab: 'outreach' },
    ] : []),
    { n: c.drafts + c.approved, t: 'Drafts', s: 'not sent', tab: 'drafts' },
    ...(d.tracking && d.tracking.bounced ? [{ n: d.tracking.bounced, t: 'Failed', s: 'address needs correcting', tab: 'outreach' }] : []),
    { n: c.sent + c.logged, t: 'Sent', s: 'messages on record', tab: 'sent' },
    { n: c.colleges_contacted, t: 'Colleges contacted', s: `of ${c.colleges} in the directory`, tab: 'contacts' },
    { n: c.contacts, t: 'Coach contacts', s: 'in the directory', tab: 'contacts' },
  ];
  return (
    <div className="space-y-5">
      {c.needs_attention > 0 && (
        <button onClick={() => go('sent')} className={`${card} w-full text-left border-[#EF5350]/50`}>
          <div className="text-[#EF5350] text-sm font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> {c.needs_attention} message{c.needs_attention > 1 ? 's' : ''} may or may not have been sent</div>
          <div className="text-[#B0BEC5] text-sm mt-1">The administrator needs to check the mailbox before anything else happens with {c.needs_attention > 1 ? 'them' : 'it'}.</div>
        </button>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((x) => (
          <button key={x.t} onClick={() => go(x.tab)} className={`${card} text-left ${x.hot ? 'border-[#00E676]/60' : ''}`}>
            <div className={`text-2xl font-bold ${x.hot ? 'text-[#00E676]' : 'text-white'}`}>{x.n}</div>
            <div className="text-sm font-semibold text-[#B0BEC5]">{x.t}</div>
            <div className="text-[11px] text-[#546E7A] mt-0.5">{x.s}</div>
          </button>
        ))}
      </div>
      <div>
        <div className="text-white font-semibold mb-2">Recent messages</div>
        {d.recent.length === 0 ? (
          <div className={`${card} text-sm text-[#B0BEC5]`}>
            Nothing yet. Press <button className="text-[#00D4FF] underline" onClick={() => go('compose')}>Compose</button> to write the first email.
          </div>
        ) : <EmailRows rows={d.recent} openEmail={openEmail} />}
      </div>
    </div>
  );
};

const EmailRows = ({ rows, openEmail }) => (
  <div className="space-y-2">
    {rows.map((e) => (
      <button key={e.id} onClick={() => openEmail(e.id)} className={`${card} w-full text-left flex flex-col sm:flex-row sm:items-center gap-2`}>
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-semibold truncate">{e.is_test && <span className="text-[10px] font-bold text-[#B0BEC5] border border-[#546E7A]/50 rounded px-1 py-0.5 mr-1.5 align-middle">TEST</span>}{e.to_school}</div>
          <div className="text-[#B0BEC5] text-xs truncate">{e.to_name || 'Coach name unknown'}{e.to_role ? ` · ${e.to_role}` : ''}</div>
          <div className="text-[#546E7A] text-xs truncate mt-0.5">{e.subject || (e.source === 'import' ? 'Subject unknown' : 'No subject yet')}</div>
        </div>
        <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1 flex-shrink-0">
          <StatusPill status={e.status} outcome={e.outcome} />
          <span className="text-[11px] text-[#546E7A]">{e.source === 'import' ? (e.sent_at ? fmtCentralDate(e.sent_at) : 'date unknown') : fmtDate(e.sent_at || e.updated_at)}</span>
        </div>
      </button>
    ))}
  </div>
);

/* ── Messages ────────────────────────────────────────────────────────────── */

const Messages = ({ api, openEmail, go, view }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/emails').then((all) => setRows(all.filter((r) => r.status !== 'cancelled'))).catch((e) => setError(e.message)); }, [api]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!rows) return <div className="text-[#546E7A] text-sm">Loading…</div>;
  const attention = rows.filter((r) => ['sending', 'uncertain'].includes(r.status));
  const groups = [
    ...(view === 'sent' && attention.length ? [{ key: 'attention', title: 'Needs checking', note: 'It is not known whether these were sent. They will not be sent again until the administrator has checked the mailbox.', rows: attention }] : []),
    ...(view === 'drafts' ? [{ key: 'draft', title: 'Drafts', note: 'Written but not sent. Open one to finish and send it.', rows: rows.filter((r) => ['draft', 'approved'].includes(r.status)) }] : []),
  ].concat(view === 'drafts' ? [] : [
    { key: 'replied', title: 'Replied', note: 'The coach wrote back.', rows: rows.filter((r) => r.outcome === 'replied') },
    { key: 'failed', title: 'Failed', note: 'A failure notice came back for these. The coach did not receive them. Correct the address in Contacts, or mark it invalid.', rows: rows.filter((r) => r.outcome === 'failed') },
    { key: 'sent', title: 'Sent', note: 'Sent, with no reply and no failure notice so far. Sending alone does not prove the message was delivered or read.', rows: rows.filter((r) => r.status === 'sent' && !r.outcome) },
    { key: 'imported', title: 'Sent earlier', note: 'Sent before the Recruiting Center and imported as a record, with no reply and no failure notice on record. Not sent through SchemeSync; cannot be edited or sent.', rows: rows.filter((r) => r.status === 'imported' && !r.outcome) },
    ...(rows.some((r) => r.status === 'imported_unverified') ? [{ key: 'unverified', title: 'Imported · sending not confirmed', note: 'Imported records with nothing to show they were actually sent. They do not count as contact with the college.', rows: rows.filter((r) => r.status === 'imported_unverified') }] : []),
  ]);
  // Only the sections that have something in them.
  const shown = groups.filter((g) => g.rows.length > 0);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[#B0BEC5] text-sm">{view === 'drafts' ? `${rows.filter((r) => ['draft', 'approved'].includes(r.status)).length} draft(s)` : `${rows.filter((r) => ['sent', 'imported'].includes(r.status)).length} sent message(s) on record`}</div>
        <button className={btnPrimary} onClick={() => go('compose')}><Pencil size={15} /> Compose</button>
      </div>
      {shown.length === 0 && (
        <div className={`${card} text-sm text-[#B0BEC5]`}>{view === 'drafts' ? 'No drafts. Press Compose to write an email.' : 'Nothing has been sent yet. Press Compose to write the first email.'}</div>
      )}
      {shown.map((g) => (
        <section key={g.key}>
          <div className="flex items-baseline gap-2 mb-1">
            <h3 className="text-white font-semibold">{g.title}</h3>
            <span className="text-[#546E7A] text-sm">{g.rows.length}</span>
          </div>
          <div className="text-[#546E7A] text-xs mb-2">{g.note}</div>
          <EmailRows rows={g.rows} openEmail={openEmail} />
        </section>
      ))}
    </div>
  );
};

/* ── Sent: one list of everything that has gone out, newest first ────────── */

const SENT_STATES = ['sent', 'imported', 'sending', 'uncertain', 'imported_unverified'];
const sentKind = (r) => (r.outcome === 'replied' ? 'replied' : r.outcome === 'failed' ? 'failed' : ['sending', 'uncertain', 'imported_unverified'].includes(r.status) ? 'check' : 'sent');

const SentList = ({ api, openEmail, go }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    api('/emails').then((all) => setRows(all.filter((r) => SENT_STATES.includes(r.status))
      // Newest first; anything without a date goes to the end.
      .sort((a, b) => (b.sent_at ? Date.parse(b.sent_at) : 0) - (a.sent_at ? Date.parse(a.sent_at) : 0) || b.id - a.id)))
      .catch((e) => setError(e.message));
  }, [api]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!rows) return <div className="text-[#546E7A] text-sm">Loading…</div>;
  const count = (k) => rows.filter((r) => sentKind(r) === k).length;
  const filters = [['all', `All ${rows.length}`], ['sent', `Sent ${count('sent')}`], ['replied', `Replied ${count('replied')}`], ['failed', `Failed ${count('failed')}`],
    ...(count('check') ? [['check', `Needs checking ${count('check')}`]] : [])];
  const needle = q.trim().toLowerCase();
  const list = rows.filter((r) => (filter === 'all' || sentKind(r) === filter)
    && (!needle || `${r.to_school} ${r.to_name || ''} ${r.to_role || ''} ${r.to_email} ${r.subject || ''}`.toLowerCase().includes(needle)));
  const when = (r) => (!r.sent_at ? 'Date unknown' : r.source === 'import' ? fmtImported(r.sent_at, r.sent_precision || 'date') : fmtDateTime(r.sent_at));
  // In this list a message from before the Recruiting Center is simply a sent message.
  const pill = (r) => <StatusPill status={r.status === 'imported' ? 'sent' : r.status} outcome={r.outcome} />;
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-3 text-[#546E7A]" />
          <input className={`${input} pl-9`} placeholder="Search college, coach, address or subject" value={q} onChange={(x) => setQ(x.target.value)} />
        </div>
        <button className={btnPrimary} onClick={() => go('compose')}><Pencil size={15} /> Compose</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {filters.map(([k, text]) => (
          <button key={k} onClick={() => setFilter(k)} className={`text-xs rounded px-2.5 py-1.5 border ${filter === k ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-[#546E7A]/40 text-[#B0BEC5]'}`}>{text}</button>
        ))}
      </div>
      {rows.length === 0 ? <div className={`${card} text-sm text-[#B0BEC5]`}>Nothing has been sent yet. Press Compose to write the first email.</div>
        : list.length === 0 ? <div className={`${card} text-sm text-[#B0BEC5]`}>No sent message matches.</div> : (
          <div className={`${card} p-0 overflow-hidden`}>
            <div className="hidden md:grid grid-cols-[1.1fr_1.5fr_1.7fr_0.9fr_auto] gap-3 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-[#546E7A] border-b border-[#546E7A]/25">
              <div>College</div><div>Coach and address</div><div>Subject</div><div>Date</div><div className="w-44">Status</div>
            </div>
            <div className="divide-y divide-[#546E7A]/20">
              {list.map((r) => (
                <button key={r.id} onClick={() => openEmail(r.id)} className="w-full text-left px-4 py-3 grid grid-cols-1 md:grid-cols-[1.1fr_1.5fr_1.7fr_0.9fr_auto] gap-x-3 gap-y-1 md:items-center text-sm hover:bg-[#243B55]/40">
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <span className="text-white font-semibold [overflow-wrap:anywhere]">{r.to_school}</span>
                    <span className="md:hidden flex-shrink-0">{pill(r)}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[#B0BEC5] truncate">{r.to_name || 'Coach name unknown'}</div>
                    <div className="text-[#546E7A] text-xs truncate">{r.to_email}</div>
                  </div>
                  <div className="text-[#B0BEC5] min-w-0 md:truncate [overflow-wrap:anywhere]">{r.subject || <span className="text-[#546E7A] italic">Subject unknown</span>}</div>
                  <div className="text-[#546E7A] text-xs md:text-sm md:text-[#B0BEC5]">{when(r)}</div>
                  <div className="hidden md:block w-44">{pill(r)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      <div className="text-[#546E7A] text-xs">“Sent” means the message went out, not that it arrived. A message is marked Replied or Failed only when a reply or a delivery failure came back.</div>
    </div>
  );
};

/* ── Discover: find football programs and their public recruiting contacts ── */

const Discover = ({ api, go, isAdmin }) => {
  const [f, setF] = useState({ division: '', conference: '', state: '', q: '', saved: '' });
  const [meta, setMeta] = useState({ conferences: [], states: [], limits: { per_request: 10 }, stored: { colleges: 0, contacts: 0 } });
  const [rows, setRows] = useState(null);          // null: nothing searched yet
  const [picked, setPicked] = useState({});        // program id -> true
  const [skip, setSkip] = useState({});            // address -> true: found, but not to be added
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const hasFilter = !!(f.division || f.conference || f.state || f.saved || f.q.trim().length >= 2);
  const search = useCallback(async () => {
    setError('');
    const qs = new URLSearchParams(Object.entries({ ...f, q: f.q.trim() }).filter(([, v]) => v)).toString();
    try {
      const r = await api(`/discovery/programs${qs ? `?${qs}` : ''}`);
      setMeta({ conferences: r.conferences, states: r.states, limits: r.limits, stored: r.stored });
      setRows(r.needs_filter ? null : r.programs);
    } catch (e) { setError(e.message); }
  }, [api, f]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);

  // While lookups are running, collect their results every few seconds.
  const running = (rows || []).filter((p) => p.lookup?.status === 'running').map((p) => p.id);
  const runningKey = running.join(',');
  useEffect(() => {
    if (!runningKey) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api(`/discovery/results?ids=${runningKey}`);
        setRows((old) => (old || []).map((p) => r.programs.find((x) => x.id === p.id) || p));
      } catch { /* try again on the next tick */ }
    }, 4000);
    return () => clearInterval(t);
  }, [api, runningKey]);

  const addable = (p) => (p.lookup?.contacts || []).filter((c) => c.confirmed && !c.already_in_directory && !skip[c.email]);
  const chosen = (rows || []).filter((p) => picked[p.id]);
  const toFind = chosen.filter((p) => !p.lookup || p.lookup.status === 'failed');
  const toAdd = chosen.filter((p) => p.lookup?.status === 'done' && addable(p).length > 0);
  const max = meta.limits.per_request;

  const find = async (list, again = false) => {
    if (again && !window.confirm(`Refresh contacts for ${list[0].college}?\n\nThis runs a new paid website lookup. The contacts already saved are kept; anything found again is marked as verified today.`)) return;
    setBusy('find'); setError(''); setDone(null);
    try {
      await api('/discovery/lookup', { method: 'POST', body: { program_ids: list.slice(0, max).map((p) => p.id), again } });
      const r = await api(`/discovery/results?ids=${list.slice(0, max).map((p) => p.id).join(',')}`);
      setRows((old) => (old || []).map((p) => r.programs.find((x) => x.id === p.id) || p));
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const forget = async (p, c) => {
    if (!window.confirm(`Delete the saved result ${c.email} for ${p.college}?\n\nThis removes it from the discovery database only. Your directory is not touched.`)) return;
    setError('');
    try {
      await api(`/discovery/contacts/${c.id}`, { method: 'DELETE' });
      setRows((old) => (old || []).map((x) => (x.id === p.id ? { ...x, lookup: { ...x.lookup, contacts: x.lookup.contacts.filter((y) => y.id !== c.id) } } : x)));
    } catch (e) { setError(e.message); }
  };
  const add = async () => {
    const n = toAdd.reduce((a, p) => a + addable(p).length, 0);
    if (!window.confirm(`Add ${toAdd.length} college(s) and ${n} contact(s) to your directory?\n\nNothing is emailed. They will appear in Outreach as Not contacted.`)) return;
    setBusy('add'); setError('');
    try {
      const r = await api('/discovery/add', { method: 'POST', body: { items: toAdd.map((p) => ({ program_id: p.id, emails: addable(p).map((c) => c.email) })) } });
      setDone(r); setPicked({});
      await search();
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const sel = 'w-full bg-[#0A1628] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/40 text-sm';
  const conferences = meta.conferences.filter((c) => !f.division || c.division === f.division);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-white font-semibold">Discover colleges</div>
        <div className="text-[#B0BEC5] text-sm">Find football programs, look up the recruiting contacts their official athletics websites list, and add the ones you want. Nothing is ever emailed from here.</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div><label className={label}>Division</label>
          <select className={sel} value={f.division} onChange={(x) => setF({ ...f, division: x.target.value, conference: '' })}>
            <option value="">Any</option><option>FBS</option><option>FCS</option><option>DII</option><option>DIII</option><option>NAIA</option><option>JUCO</option>
          </select></div>
        <div><label className={label}>Conference</label>
          <select className={sel} value={f.conference} onChange={(x) => setF({ ...f, conference: x.target.value })}>
            <option value="">Any</option>{conferences.map((c) => <option key={`${c.division}${c.conference}`} value={c.conference}>{c.conference}{f.division ? '' : ` (${c.division})`}</option>)}
          </select></div>
        <div><label className={label}>State</label>
          <select className={sel} value={f.state} onChange={(x) => setF({ ...f, state: x.target.value })}>
            <option value="">Any</option>{meta.states.map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <div><label className={label}>College name</label>
          <input className={input} value={f.q} onChange={(x) => setF({ ...f, q: x.target.value })} placeholder="e.g. Samford" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-[#B0BEC5] cursor-pointer">
        <input type="checkbox" checked={!!f.saved} onChange={(x) => setF({ ...f, saved: x.target.checked ? 'true' : '' })} />
        Only colleges with saved contacts <span className="text-[#546E7A]">({meta.stored.colleges} colleges · {meta.stored.contacts} contacts saved)</span>
      </label>
      <ErrorNote>{error}</ErrorNote>
      {done && (
        <div role="status" className="rounded-lg border border-[#00E676]/50 bg-[#00E676]/10 px-3 py-2 text-sm text-[#00E676]">
          <b>Added {done.colleges_added} college(s) and {done.contacts_added} contact(s).</b>
          {done.colleges_already_there > 0 && ` ${done.colleges_already_there} college(s) were already in your directory and got the new contacts.`}
          {done.contacts_already_there > 0 && ` ${done.contacts_already_there} address(es) were already there and were left alone.`}
          {' '}They are in <button className="underline" onClick={() => go('outreach')}>Outreach</button> as Not contacted. Nothing was emailed.
        </div>
      )}
      {!hasFilter || rows === null ? (
        <div className={`${card} text-sm text-[#B0BEC5]`}>Choose a division, conference or state, type a college or coach name, or check “Only colleges with saved contacts”. Saved contacts appear at once, with no new lookup.</div>
      ) : rows.length === 0 ? (
        <div className={`${card} text-sm text-[#B0BEC5]`}>No football program matches.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[#B0BEC5] text-sm mr-auto">{rows.length} program(s) · {chosen.length} selected</span>
            <button className={btnGhost} onClick={() => setPicked(chosen.length ? {} : Object.fromEntries(rows.slice(0, 50).map((p) => [p.id, true])))}>{chosen.length ? 'Clear selection' : rows.length > 50 ? 'Select first 50' : 'Select all'}</button>
            <button className={btnGhost} disabled={!!busy || toFind.length === 0} onClick={() => find(toFind)}>
              <Search size={14} /> {busy === 'find' ? 'Starting…' : `Find contacts (${Math.min(toFind.length, max)})`}
            </button>
            <button className={btnPrimary} disabled={!!busy || toAdd.length === 0} onClick={add}>
              <Plus size={15} /> {busy === 'add' ? 'Adding…' : `Add to directory (${toAdd.length})`}
            </button>
          </div>
          <div className="text-[#546E7A] text-xs">Find contacts runs a paid website lookup only for selected colleges with nothing saved yet. Colleges with saved contacts are never looked up again unless you press Refresh contacts.</div>
          {toFind.length > max && <div className="text-[#FFB300] text-xs">Contacts are looked up {max} colleges at a time. Press Find contacts again for the rest.</div>}
          <div className="space-y-2">
            {rows.map((p) => {
              const l = p.lookup; const contacts = l?.contacts || []; const can = addable(p).length;
              return (
                <div key={p.id} className={`${card} ${picked[p.id] ? 'border-[#00D4FF]/60' : ''}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 w-4 h-4 flex-shrink-0" checked={!!picked[p.id]} onChange={(x) => setPicked({ ...picked, [p.id]: x.target.checked })} />
                    <span className="min-w-0 flex-1">
                      <span className="text-white font-semibold [overflow-wrap:anywhere]">{p.college}</span>
                      <span className="block text-[#B0BEC5] text-xs">{[p.division, p.conference, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}</span>
                      {p.questionnaire_url && <a href={p.questionnaire_url} target="_blank" rel="noopener noreferrer" onClick={(x) => x.stopPropagation()} className="inline-flex items-center gap-1 text-[#00D4FF] text-xs underline">Recruiting questionnaire <ExternalLink size={11} /></a>}
                    </span>
                    <span className="flex flex-col items-end gap-1 flex-shrink-0">
                      {p.in_directory && <Tag cls="text-[#B39DDB] border-[#B39DDB]/40">In your directory · {p.contacts_on_file}</Tag>}
                      {l?.status === 'running' && <Tag cls="text-[#FFB300] border-[#FFB300]/40">Looking…</Tag>}
                      {l?.status === 'done' && can > 0 && <Tag cls="text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10">{can} to add</Tag>}
                      {l?.status === 'done' && can === 0 && <Tag cls="text-[#546E7A] border-[#546E7A]/40">{contacts.some((c) => c.already_in_directory) ? 'Already added' : 'No public email'}</Tag>}
                      {l?.status === 'failed' && <Tag cls="text-[#EF5350] border-[#EF5350]/40">Lookup failed</Tag>}
                    </span>
                  </label>
                  {l?.status === 'running' && <div className="text-[#546E7A] text-xs mt-2 ml-7">Reading the official athletics website. This takes about a minute; you can keep working.</div>}
                  {l?.status === 'failed' && <div className="text-[#EF5350] text-xs mt-2 ml-7">{l.error} Select it and press Find contacts to try again.</div>}
                  {l && (l.status === 'done' || contacts.length > 0) && (
                    <div className="mt-3 ml-7 space-y-2">
                      {l.last_verified_at && <div className="text-[#B0BEC5] text-xs">Saved contacts · last verified on the website {fmtDate(l.last_verified_at)}</div>}
                      {l.status === 'done' && l.error && <div className="text-[#FFB300] text-xs">The last refresh did not finish ({l.error}) The saved contacts below are unchanged.</div>}
                      {l.status === 'done' && contacts.length === 0 && <div className="text-[#B0BEC5] text-sm">No football staff email address is published on the official website, so this college cannot be added from here.</div>}
                      {contacts.map((c) => (
                        <div key={c.email} className="flex items-start gap-2">
                          <label className={`flex items-start gap-2 text-sm min-w-0 flex-1 ${c.confirmed && !c.already_in_directory ? 'cursor-pointer' : 'opacity-60'}`}>
                            <input type="checkbox" className="mt-1 flex-shrink-0" disabled={!c.confirmed || c.already_in_directory} checked={!!c.confirmed && !c.already_in_directory && !skip[c.email]} onChange={(x) => setSkip({ ...skip, [c.email]: !x.target.checked })} />
                            <span className="min-w-0">
                              <span className="text-white">{c.name || 'Name not listed'}</span>{c.title && <span className="text-[#B0BEC5]"> · {c.title}</span>}
                              <span className="block text-[#B0BEC5] [overflow-wrap:anywhere]">{c.email}</span>
                              <span className="block text-xs text-[#546E7A] [overflow-wrap:anywhere]">
                                {c.already_in_directory ? 'Already in your directory · ' : !c.confirmed ? 'Could not be confirmed on the page, so it cannot be added · ' : ''}
                                Verified {fmtDate(c.last_verified_at)}{c.not_found_at_last_check ? ' · not found at the last refresh' : ''} · Source: <a className="text-[#00D4FF] underline" href={c.source_url} target="_blank" rel="noopener noreferrer" onClick={(x) => x.stopPropagation()}>{c.source_url.replace(/^https?:\/\//, '')} <ExternalLink size={11} className="inline" /></a>
                              </span>
                            </span>
                          </label>
                          {isAdmin && <button className="text-[#546E7A] hover:text-[#EF5350] p-1 flex-shrink-0" aria-label={`Delete saved result ${c.email}`} onClick={() => forget(p, c)}><Trash2 size={14} /></button>}
                        </div>
                      ))}
                      {l.note && <div className="text-[#546E7A] text-xs [overflow-wrap:anywhere]">{l.note}</div>}
                      {l.status !== 'running' && <button className="text-[#00D4FF] text-xs underline" disabled={!!busy} onClick={() => find([p], true)}><RefreshCw size={11} className="inline" /> Refresh contacts (new paid lookup)</button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="text-[#546E7A] text-xs">College list: College Football Data. Contacts come only from each college’s official website, with the page and the date last verified shown. Everything found is saved until you refresh or delete it. Staff change often, so check an old address before relying on it.</div>
    </div>
  );
};

/* ── Questionnaires: fill one in, then let the school know ───────────────── */

const Questionnaires = ({ api, openEmail }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('todo');
  const [division, setDivision] = useState('');
  const load = useCallback(() => api('/questionnaires').then(setD).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); }, [load]);
  if (!d) return error ? <ErrorNote>{error}</ErrorNote> : <div className="text-[#546E7A] text-sm">Loading…</div>;
  const done = d.schools.filter((x) => x.completed_at).length;
  const needle = q.trim().toLowerCase();
  const list = d.schools.filter((x) => (show === 'all' || (show === 'done' ? x.completed_at : !x.completed_at))
    && (!division || x.division === division) && (!needle || `${x.school} ${x.conference || ''} ${x.state || ''}`.toLowerCase().includes(needle)));
  const complete = async (x) => {
    setError(''); setMsg(null);
    let to = null;
    try { to = await api(`/questionnaires/${x.program_id}/recipient`); } catch (e) { setError(e.message); return; }
    const who = to.email ? `${to.name ? `${to.name} <${to.email}>` : to.email}` : null;
    const ok = window.confirm(who
      ? `Mark the ${x.school} questionnaire as complete?\n\nThis sends a short note to ${who}, ${to.why}, letting them know it is done.`
      : `Mark the ${x.school} questionnaire as complete?\n\nThere is no email address on file for ${x.school}, so no note will be sent.`);
    if (!ok) return;
    setBusy(x.program_id);
    try {
      const r = await api(`/questionnaires/${x.program_id}/complete`, { method: 'POST', body: { send_note: !!who } });
      setMsg(r.sent ? { ok: true, text: `${x.school} marked complete. A note was sent to ${r.to}.` }
        : { ok: !who, text: `${x.school} marked complete. ${r.reason || ''}` });
      await load();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };
  const undo = async (x) => {
    if (!window.confirm(`Mark ${x.school} as not done?${x.email_status === 'sent' ? '\n\nThe note already sent to the school stays sent.' : ''}`)) return;
    try { await api(`/questionnaires/${x.program_id}/undo`, { method: 'POST' }); await load(); } catch (e) { setError(e.message); }
  };
  const sel = 'bg-[#0A1628] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/40 text-sm';
  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <div className="text-white font-semibold">Recruiting questionnaires</div>
        <div className="text-[#B0BEC5] text-sm">Open a school’s questionnaire, fill it in, then come back and press <b className="text-white">Mark complete</b>. The school gets a short note letting them know it is done.</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[[d.schools.length, 'Schools with a questionnaire'], [done, 'Completed'], [d.schools.length - done, 'Still to do']].map(([n, t]) => (
          <div key={t} className={card}><div className="text-xl font-bold text-white">{n}</div><div className="text-xs text-[#B0BEC5]">{t}</div></div>
        ))}
      </div>
      {d.note_preview && (
        <details className={`${card} text-sm`}>
          <summary className="cursor-pointer text-[#00D4FF]">The note sent when you mark one complete</summary>
          <div className="mt-2 text-white font-semibold [overflow-wrap:anywhere]">{d.note_preview.subject}</div>
          <div className="mt-1 text-[#B0BEC5] whitespace-pre-wrap [overflow-wrap:anywhere]">{d.note_preview.body}</div>
          <div className="mt-2 text-[#546E7A] text-xs">“Example University” is replaced with each school’s name. It is sent from the recruiting mailbox, with the usual private copy to Steve.</div>
        </details>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input className={`${input} flex-1`} placeholder="Find a school, conference or state" value={q} onChange={(x) => setQ(x.target.value)} />
        <select className={sel} value={division} onChange={(x) => setDivision(x.target.value)}><option value="">All divisions</option><option>FBS</option><option>FCS</option></select>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[['todo', `To do ${d.schools.length - done}`], ['done', `Completed ${done}`], ['all', `All ${d.schools.length}`]].map(([k, t]) => (
          <button key={k} onClick={() => setShow(k)} className={`text-xs rounded px-2.5 py-1.5 border ${show === k ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-[#546E7A]/40 text-[#B0BEC5]'}`}>{t}</button>
        ))}
      </div>
      <ErrorNote>{error}</ErrorNote>
      {msg && <div role="status" className={`rounded-lg border px-3 py-2 text-sm ${msg.ok ? 'border-[#00E676]/50 bg-[#00E676]/10 text-[#00E676]' : 'border-[#FFB300]/50 bg-[#FFB300]/10 text-[#FFB300]'}`}>{msg.text}</div>}
      {list.length === 0 ? <div className={`${card} text-sm text-[#B0BEC5]`}>{show === 'done' ? 'None completed yet.' : 'No school matches.'}</div> : (
        <div className="space-y-2">
          {list.map((x) => (
            <div key={x.program_id} className={`${card} ${x.completed_at ? 'border-[#00E676]/40' : ''}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-semibold [overflow-wrap:anywhere]">{x.school}</div>
                  <div className="text-[#B0BEC5] text-xs">{[x.division, x.conference, x.state].filter(Boolean).join(' · ')}</div>
                  {x.kind && x.kind !== 'football' && <div className="text-[#546E7A] text-xs mt-0.5">If the form asks for a sport, choose Football.</div>}
                </div>
                {x.completed_at && <Tag cls="text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10">Completed</Tag>}
              </div>
              {x.completed_at ? (
                <div className="mt-2 text-xs text-[#B0BEC5] flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>Marked complete {fmtDateTime(x.completed_at)}{x.completed_by ? ` by ${x.completed_by}` : ''}</span>
                  {x.email_id && <button className="text-[#00D4FF] underline" onClick={() => openEmail(x.email_id)}>{x.email_status === 'sent' ? `Note sent to ${x.emailed_to}` : 'Note not sent: open it'}</button>}
                  <button className="text-[#546E7A] underline" onClick={() => undo(x)}>Undo</button>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={x.url} target="_blank" rel="noopener noreferrer" className={btnGhost}><ExternalLink size={14} /> Open questionnaire</a>
                  <button className={btnPrimary} disabled={busy === x.program_id} onClick={() => complete(x)}><CheckCircle2 size={14} /> {busy === x.program_id ? 'Sending…' : 'Mark complete'}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="text-[#546E7A] text-xs">Links were found on each school’s official athletics website. If one no longer works, the school’s football page usually has a “Recruiting questionnaire” link.</div>
    </div>
  );
};

/* ── Campaign: the standard email to every college not yet contacted ─────── */

const CAMPAIGN_STATE = {
  running: { text: 'Running', cls: 'text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10' },
  paused: { text: 'Paused', cls: 'text-[#FFB300] border-[#FFB300]/40 bg-[#FFB300]/10' },
  stopped: { text: 'Stopped', cls: 'text-[#EF5350] border-[#EF5350]/40 bg-[#EF5350]/12' },
  completed: { text: 'Completed', cls: 'text-[#00D4FF] border-[#00D4FF]/40 bg-[#00D4FF]/10' },
};

const Campaign = ({ api, openEmail, go }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api('/campaign').then(setD).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!d) return error ? <ErrorNote>{error}</ErrorNote> : <div className="text-[#546E7A] text-sm">Loading…</div>;
  const c = d.campaign; const active = c && ['running', 'paused'].includes(c.status);
  const act = async (what) => {
    setBusy(what); setError('');
    try { setD(await api(`/campaign/${what}`, { method: 'POST' })); } catch (e) { setError(e.message); }
    setBusy('');
  };
  const span = d.hours_remaining >= 1 ? `about ${Math.round(d.hours_remaining)} hour${Math.round(d.hours_remaining) === 1 ? '' : 's'}` : 'under an hour';
  const notReady = !d.ready.template ? 'No standard recruiting email has been set up.' : !d.ready.mailbox_connected ? 'The recruiting mailbox is not connected.' : !d.ready.coach_sending ? 'Sending to coaches is turned off on the server.' : d.eligible === 0 ? 'There is no college left to contact that has a valid email address.' : '';
  const total = c ? c.sent + c.remaining : 0;
  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <div className="text-white font-semibold">Email campaign</div>
        <div className="text-[#B0BEC5] text-sm">Sends the standard recruiting email to one contact at every college that has not been contacted, {d.batch_size} at a time with a {d.pause_minutes}-minute pause between batches, until none are left.{d.quiet_hours ? ` Nothing is sent from ${d.quiet_hours}.` : ''} It runs on the server; you do not need to keep this page open.</div>
      </div>
      <ErrorNote>{error}</ErrorNote>
      <div className="text-[#546E7A] text-xs">{d.runner_seen_at ? `The server last checked for campaign work at ${fmtDateTime(d.runner_seen_at)}.` : 'The server’s campaign runner is starting.'}</div>

      {d.catalog && (
        <div className={`${card} space-y-3`}>
          <div className="text-white font-semibold">College catalog</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[d.catalog.total, 'College programs', 'NCAA, NAIA and junior college'],
              [d.catalog.with_verified_address, 'With a verified address', 'seen on the official website'],
              [d.catalog.awaiting_research, 'Awaiting research', 'website not checked yet'],
              [d.catalog.no_public_address, 'No public address', 'none found on the website'],
              [d.catalog.already_contacted, 'Already contacted', 'never emailed again'],
              [d.catalog.remaining, 'Remaining to email', 'one email each'],
              [c ? c.sent_today : 0, 'Sent today', `${d.batch_size} per batch`],
              [d.catalog.hours_remaining >= 1 ? `${Math.round(d.catalog.hours_remaining)} h` : '< 1 h', 'Estimated time left', `${d.catalog.batches_remaining} batch${d.catalog.batches_remaining === 1 ? '' : 'es'}, ${d.pause_minutes} min apart`]].map(([n, a, b]) => (
              <div key={a}><div className="text-xl font-bold text-white">{n}</div><div className="text-xs font-semibold text-[#B0BEC5]">{a}</div><div className="text-[11px] text-[#546E7A]">{b}</div></div>
            ))}
          </div>
          {d.research.running
            ? <div className="text-[#FFB300] text-sm">Reading official athletics websites: {d.research.done} of {d.research.total} colleges checked, {d.research.found} with addresses so far. This runs on the server and costs nothing.</div>
            : d.can_control && d.catalog.awaiting_research > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <button className={btnGhost} disabled={!!busy} onClick={() => act('research')}><Search size={14} /> Research {d.catalog.awaiting_research} remaining colleges</button>
                <span className="text-[#546E7A] text-xs flex-1 min-w-[12rem]">Reads each college’s official athletics website for football staff addresses. Free: no AI request is made.</span>
              </div>
            )}
          {d.catalog.excluded?.length > 0 && (
            <details className="text-xs text-[#546E7A]">
              <summary className="cursor-pointer">{d.catalog.excluded.length} colleges are kept out of campaigns</summary>
              <div className="mt-1 space-y-0.5 text-[#B0BEC5]">
                {Object.entries(d.catalog.excluded.reduce((g, x) => ({ ...g, [x.reason]: (g[x.reason] || 0) + 1 }), {})).map(([reason, n]) => (
                  <div key={reason}>{n} · {reason}</div>
                ))}
              </div>
            </details>
          )}
          {d.catalog.outside_catalog > 0 && <div className="text-[#546E7A] text-xs">{d.catalog.outside_catalog} college(s) in your directory are not in the catalog; they are included in the campaign in the same way.</div>}
        </div>
      )}

      {active || (c && ['stopped', 'completed'].includes(c.status)) ? (
        <div className={`${card} space-y-3 ${c.status === 'running' ? 'border-[#00E676]/50' : ''}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Tag cls={CAMPAIGN_STATE[c.status].cls}>{CAMPAIGN_STATE[c.status].text}</Tag>
            <span className="text-[#B0BEC5] text-sm">Started {fmtDateTime(c.started_at)}{c.ended_at ? ` · ended ${fmtDateTime(c.ended_at)}` : ''}</span>
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-2 text-sm"><span className="text-white font-semibold">{c.sent} sent</span><span className="text-[#B0BEC5]">{active ? `${c.remaining} to go` : `${c.sent} college(s) emailed`}</span></div>
            <div className="h-2 rounded-full bg-[#243B55] overflow-hidden mt-1.5"><div className="h-full bg-[#00E676]" style={{ width: `${total ? Math.round((c.sent / total) * 100) : 100}%` }} /></div>
          </div>
          {active && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-[#546E7A] text-xs">This batch</div><div className="text-white">{c.between_batches ? `${c.daily_limit} of ${c.daily_limit} · complete` : `${c.batch_sent} of ${c.daily_limit}`}</div></div>
              <div><div className="text-[#546E7A] text-xs">Next email</div><div className="text-white">{c.status === 'paused' ? 'Paused' : c.remaining === 0 ? 'Finishing' : d.quiet_until ? `overnight pause · resumes ${fmtDateTime(d.quiet_until)}` : c.between_batches ? `next batch at ${fmtDateTime(c.next_send_at)}` : 'within a minute'}</div></div>
            </div>
          )}
          {c.paused_reason && c.status === 'paused' && <div className="rounded-lg border border-[#FFB300]/40 bg-[#FFB300]/10 px-3 py-2 text-sm text-[#FFB300] [overflow-wrap:anywhere]">{c.paused_reason}</div>}
          {c.last_error && c.status === 'running' && <div className="text-[#FFB300] text-xs [overflow-wrap:anywhere]">Last attempt was not sent ({c.last_error}). It will try again at {fmtDateTime(c.next_send_at)}.</div>}
          {c.needs_checking > 0 && <div className="rounded-lg border border-[#EF5350]/40 bg-[#EF5350]/10 px-3 py-2 text-sm text-[#EF5350]">{c.needs_checking} email(s) may or may not have gone out. They are in <button className="underline" onClick={() => go('sent')}>Sent</button> under Needs checking and will not be sent again.</div>}
          {c.status === 'completed' && <div className="text-[#00D4FF] text-sm">Every eligible college has received one email. The campaign has ended.</div>}
          {d.can_control && active && (
            <div className="flex flex-wrap gap-2 pt-1">
              {c.status === 'running' && <button className={btnGhost} disabled={!!busy} onClick={() => act('pause')}><Pause size={14} /> {busy === 'pause' ? 'Pausing…' : 'Pause'}</button>}
              {c.status === 'paused' && <button className={btnPrimary} disabled={!!busy} onClick={() => act('resume')}><Play size={14} /> {busy === 'resume' ? 'Resuming…' : 'Resume'}</button>}
              <button className={btnDanger} disabled={!!busy} onClick={() => act('stop')}><Square size={14} /> {busy === 'stop' ? 'Stopping…' : 'Stop'}</button>
            </div>
          )}
        </div>
      ) : null}

      {!active && (
        <div className={`${card} space-y-3`}>
          <div className="text-white font-semibold">{c ? 'Start a new campaign' : 'Start campaign'}</div>
          <dl className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm">
            <dt className="text-[#546E7A]">Colleges to email</dt><dd className="text-white">{d.eligible} <span className="text-[#B0BEC5]">· one email each</span></dd>
            <dt className="text-[#546E7A]">Pace</dt><dd className="text-white">{d.batch_size} at a time, then a {d.pause_minutes}-minute pause <span className="text-[#B0BEC5]">· {d.batches_remaining} batch{d.batches_remaining === 1 ? '' : 'es'} · {span}{d.quiet_hours ? ` · none from ${d.quiet_hours}` : ''}</span></dd>
            <dt className="text-[#546E7A]">Email</dt><dd className="text-white [overflow-wrap:anywhere]">{d.template_subject || 'none set'}</dd>
          </dl>
          {d.can_control ? (
            <>
              <div className="text-[#FFB300] text-sm">Pressing Start is the authorization to send. Emails then go out automatically with no further review or confirmation, until you pause or stop the campaign or every college has been emailed.</div>
              {notReady && <div className="text-[#EF5350] text-sm">{notReady}</div>}
              <button className={btnPrimary} disabled={!!busy || !!notReady} onClick={() => act('start')}><Play size={15} /> {busy === 'start' ? 'Starting…' : 'Start Campaign'}</button>
            </>
          ) : <div className="text-[#B0BEC5] text-sm">Only the administrator can start a campaign.</div>}
        </div>
      )}

      {(d.left_out.do_not_contact > 0 || d.left_out.no_valid_email > 0) && (
        <div className="text-[#B0BEC5] text-sm">Left out: {[d.left_out.no_valid_email > 0 && `${d.left_out.no_valid_email} college(s) with no valid email address`, d.left_out.do_not_contact > 0 && `${d.left_out.do_not_contact} marked do-not-contact`].filter(Boolean).join(' · ')}. Colleges already contacted, including earlier outreach, are never emailed again.</div>
      )}

      {d.next_up.length > 0 && (!c || c.status !== 'completed') && (
        <div className={card}>
          <div className="text-white font-semibold mb-2">{active ? 'Next up' : 'Who would be emailed first'} <span className="text-[#546E7A] font-normal text-sm">· {d.eligible} in all</span></div>
          <div className="divide-y divide-[#546E7A]/20">
            {d.next_up.map((n) => (
              <div key={`${n.college}${n.email}`} className="py-2 first:pt-0 last:pb-0 text-sm">
                <div className="text-white">{n.college}</div>
                <div className="text-[#546E7A] text-xs [overflow-wrap:anywhere]">{n.name || 'Name not listed'}{n.title ? ` · ${n.title}` : ''} · {n.email}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {c && c.recent.length > 0 && (
        <div className={card}>
          <div className="text-white font-semibold mb-2">Sent by this campaign <span className="text-[#546E7A] font-normal text-sm">· {c.sent}</span></div>
          <div className="divide-y divide-[#546E7A]/20">
            {c.recent.map((r) => (
              <button key={r.email_id} onClick={() => openEmail(r.email_id)} className="w-full text-left py-2 first:pt-0 last:pb-0 text-sm flex items-start justify-between gap-3">
                <span className="min-w-0"><span className="text-white">{r.college}</span><span className="block text-[#546E7A] text-xs [overflow-wrap:anywhere]">{r.name || 'Name not listed'} · {r.email}</span></span>
                <span className="text-[#B0BEC5] text-xs whitespace-nowrap">{fmtDateTime(r.sent_at)}</span>
              </button>
            ))}
          </div>
          {c.sent > c.recent.length && <button className="text-[#00D4FF] text-xs underline mt-2" onClick={() => go('sent')}>See all in Sent</button>}
        </div>
      )}
    </div>
  );
};

/* ── Compose: choose a coach, then write ─────────────────────────────────── */

const Compose = ({ api, openEmail, go }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { api('/contacts').then(setRows).catch((e) => setError(e.message)); }, [api]);
  const start = async (c) => {
    setError('');
    try { openEmail((await api('/emails', { method: 'POST', body: { contact_id: c.id } })).id); } catch (e) { setError(e.message); }
  };
  const needle = q.trim().toLowerCase();
  const list = (rows || []).filter((c) => !needle || `${c.full_name || ''} ${c.college_name} ${c.email} ${c.title || ''}`.toLowerCase().includes(needle));
  const cannot = (c) => (c.do_not_contact ? 'Do not contact' : ['invalid', 'outdated'].includes(c.email_status) ? `Address ${c.email_status}`  : '');
  return (
    <div className="space-y-3 max-w-3xl">
      <div className="text-white font-semibold">New email: who is it to?</div>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-3 text-[#546E7A]" />
        <input autoFocus className={`${input} pl-9`} placeholder="Type a coach, college or address" value={q} onChange={(x) => setQ(x.target.value)} />
      </div>
      <ErrorNote>{error}</ErrorNote>
      {!rows ? <div className="text-[#546E7A] text-sm">Loading…</div> : (
        <div className={`${card} p-0 divide-y divide-[#546E7A]/20 overflow-hidden`}>
          {list.length === 0 && <div className="px-4 py-3 text-sm text-[#546E7A]">No coach matches.</div>}
          {list.map((c) => (
            <button key={c.id} disabled={!!cannot(c)} onClick={() => start(c)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#243B55]/40 disabled:opacity-50 disabled:hover:bg-transparent">
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-medium [overflow-wrap:anywhere]">{c.full_name || 'Name unknown'} <span className="text-[#B0BEC5] font-normal">· {c.college_name}</span></div>
                <div className="text-[#546E7A] text-xs [overflow-wrap:anywhere]">{c.email}{c.title ? ` · ${c.title}` : ''}</div>
              </div>
              {cannot(c) ? <span className="text-[#EF5350] text-[11px] font-bold uppercase whitespace-nowrap">{cannot(c)}</span>
                : <><AddressTag status={c.email_status} /><Mail size={15} className="text-[#00D4FF] flex-shrink-0" /></>}
            </button>
          ))}
        </div>
      )}
      <div className="text-[#546E7A] text-xs">Not listed? <button className="text-[#00D4FF] underline" onClick={() => go('contacts')}>Add the coach in Contacts</button> first.</div>
    </div>
  );
};

/* ── One message: write and send ───────────────────────────────────────── */

const Recipients = ({ e }) => (
  <dl className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2 gap-y-2 text-sm">
    {/* The exact sender a coach will see, shown wherever the recipients are. */}
    <dt className="text-[#546E7A]">From</dt>
    <dd>{e.from_identity ? <span className="text-white break-words">{e.from_identity}</span> : <span className="text-[#FFB300]">No sender name has been set</span>}</dd>
    <dt className="text-[#546E7A]">To</dt>
    <dd>
      <div className="text-white">{e.to_name || <span className="text-[#B0BEC5]">Name unknown</span>}</div>
      <div className="text-[#B0BEC5] break-all">{e.to_email}</div>
      <div className="text-[#546E7A] text-xs mt-0.5">{[e.to_role, e.to_school].filter(Boolean).join(' · ')}</div>
    </dd>
    <dt className="text-[#546E7A]">CC</dt>
    <dd>{e.cc_email ? <span className="text-[#B0BEC5] break-all">{e.cc_email}</span> : <span className="text-[#546E7A]">none</span>}</dd>
    {e.bcc_email && (<>
      <dt className="text-[#546E7A]">BCC</dt>
      <dd><span className="text-[#B0BEC5] break-all">{e.bcc_email}</span><span className="text-[#546E7A] text-xs"> · private copy, added automatically; the coach does not see it</span></dd>
    </>)}
  </dl>
);

const PriorContact = ({ prior, school }) => (<>
  {prior.messages.length === 0 && (prior.uncertain || []).length > 0 && (
    <div className="rounded-lg border border-[#546E7A]/50 bg-[#243B55]/40 px-3 py-2.5 text-sm">
      <div className="text-[#B0BEC5] font-semibold">{school} may have been contacted before</div>
      <ul className="text-[#B0BEC5] mt-1.5 space-y-0.5">
        {prior.uncertain.map((m) => <li key={m.id}>{m.to_name || 'Coach name unknown'} · {m.how}</li>)}
      </ul>
      <div className="text-[#546E7A] mt-1.5 text-xs">An imported record exists, but nothing shows it was sent. Worth a look.</div>
    </div>
  )}
  {prior.messages.length ? (
  <div className="rounded-lg border border-[#FFB300]/40 bg-[#FFB300]/10 px-3 py-2.5 text-sm">
    <div className="flex items-center gap-1.5 text-[#FFB300] font-semibold"><AlertTriangle size={15} /> {school} has already been contacted</div>
    <ul className="text-[#B0BEC5] mt-1.5 space-y-0.5">
      {prior.messages.map((m) => (
        <li key={m.id}>{m.sent_at ? (m.sent_precision ? fmtCentralDate(m.sent_at) : fmtDate(m.sent_at)) : 'date unknown'} · {m.to_name || 'coach name unknown'}{m.to_role ? ` (${m.to_role})` : ''} · {m.how}</li>
      ))}
    </ul>
    <div className="text-[#B0BEC5] mt-1.5">A follow-up is fine.</div>
  </div>
  ) : null}
</>);

const EmailEditor = ({ api, id, back, isAdmin }) => {
  const [e, setE] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [resolveNote, setResolveNote] = useState('');

  const adopt = useCallback((d) => {
    setE(d); setSubject(d.subject || '');
    // A new message starts with the standard signature already in place.
    // A new message opens with the standard greeting (a reply does not).
    setBody(d.body || (d.status === 'draft' && d.sign_off ? `${d.answers || !d.greeting ? '' : `${d.greeting}\n`}\n\n${d.sign_off}` : '')); 
  }, []);
  useEffect(() => { api(`/emails/${id}`).then(adopt).catch((x) => setError(x.message)); }, [api, id, adopt]);

  const run = async (name, fn) => {
    setBusy(name); setError('');
    try { await fn(); } catch (x) { setError(x.message); } finally { setBusy(''); }
  };

  if (!e) return error ? <ErrorNote>{error}</ErrorNote> : <div className="text-[#546E7A] text-sm">Loading…</div>;

  const dirty = subject !== (e.subject || '') || body.trim() !== (e.body || '').trim();
  const isDraft = ['draft', 'approved'].includes(e.status);
  const hasPrior = e.prior_contact.messages.length > 0;
  const blocked = e.contact?.do_not_contact;
  const ruleProblems = e.rule_problems || [];

  const save = () => run('save', async () => adopt(await api(`/emails/${id}`, { method: 'PATCH', body: { subject, body } })));
  const useTemplate = () => run('template', async () => {
    if ((body.trim() || subject.trim()) && !window.confirm('Replace the subject and message with the standard recruiting email?')) return;
    adopt(await api(`/emails/${id}/use-template`, { method: 'POST' }));
  });
  const reload = () => api(`/emails/${id}`).then(adopt).catch(() => {});
  const send = () => run('send', async () => {
    const who = `${e.to_name ? `${e.to_name} <${e.to_email}>` : e.to_email}${e.cc_email ? `\nCopy to: ${e.cc_email}` : ''}`;
    const again = hasPrior && !e.answers ? `\n\nNote: ${e.to_school} has already been contacted.` : '';
    if (!window.confirm(`Send this email now?\n\nTo: ${who}\nSubject: ${subject.trim()}${again}\n\nIt goes out immediately from ${e.sending.sender_email} and cannot be recalled.`)) return;
    try {
      adopt(await api(`/emails/${id}/send-now`, { method: 'POST', body: { subject, body, confirm: true } }));
    } catch (x) {
      // Whatever happened, show the message as the server now has it.
      await reload();
      throw x;
    }
  });
  const resolve = (outcome) => run('resolve', async () => adopt(await api(`/emails/${id}/resolve-uncertain`, {
    method: 'POST', body: { outcome, note: resolveNote },
  })));
  const removeRecord = () => run('delete', async () => {
    const what = e.status === 'sent' ? 'This deletes the record only. The email itself was sent and cannot be recalled.'
      : ['imported', 'imported_unverified'].includes(e.status) ? 'This deletes the imported record.' : 'This deletes the message.';
    if (!window.confirm(`Delete this record for ${e.to_name || e.to_email} (${e.to_school}) permanently?\n\n${what} This cannot be undone.`)) return;
    await api(`/emails/${id}/record`, { method: 'DELETE' });
    back();
  });
  const discard = () => run('discard', async () => {
    if (!window.confirm('Discard this message? It will not be sent.')) return;
    await api(`/emails/${id}/cancel`, { method: 'POST' });
    back();
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <button onClick={back} className="text-[#B0BEC5] text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back</button>

      <div className={card}>
        <div className={`flex items-start justify-between gap-3 ${e.source === 'import' ? '' : 'mb-3'}`}>
          <div className="text-white font-semibold">{e.to_school}</div>
          <StatusPill status={e.status} outcome={e.outcome} />
        </div>
        {e.source !== 'import' && <Recipients e={e} />}
      </div>

      {e.outcome === 'failed' && (
        <div className={`${card} border-[#EF5350]/50`}>
          <div className="text-[#EF5350] text-sm font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> Failed</div>
          <div className="text-[#B0BEC5] text-sm mt-1">A failure notice came back for {e.to_email}. The coach did not receive this message. Correct the address in Contacts, or mark it invalid.</div>
        </div>
      )}
      {e.outcome === 'replied' && (
        <div className={`${card} border-[#00E676]/50`}>
          <div className="text-[#00E676] text-sm font-semibold flex items-center gap-1.5"><CheckCircle2 size={15} /> Replied</div>
          <div className="text-[#B0BEC5] text-sm mt-1">A reply from {e.to_email} is in the Inbox.</div>
        </div>
      )}
      {['invalid', 'outdated', 'unverified'].includes(e.contact?.email_status) && ['draft', 'approved'].includes(e.status) && (
        <ErrorNote>This address is marked {e.contact.email_status} in Contacts.{e.contact.email_status === 'unverified' ? ' Check it before sending.' : ' The message cannot be sent until the address is corrected.'}</ErrorNote>
      )}
      {blocked && <ErrorNote>This recipient is marked do-not-contact in the directory. The message cannot be approved or sent.</ErrorNote>}
      {/* Only relevant while the message can still be changed or sent. */}
      {['draft', 'approved'].includes(e.status) && <PriorContact prior={e.prior_contact} school={e.to_school} />}
      <ErrorNote>{error}</ErrorNote>

      {/* ── Sent or logged: a record, nothing to do ── */}
      {/* ── In flight, or outcome unknown ── */}
      {e.status === 'sending' && (
        <div className={`${card} border-[#FFB300]/50`}>
          <div className="text-[#FFB300] text-sm font-semibold">This message is being sent…</div>
          <button className={`${btnGhost} mt-3`} onClick={reload}>Check again</button>
        </div>
      )}
      {e.status === 'uncertain' && (
        <div className={`${card} border-[#EF5350]/50`}>
          <div className="flex items-center gap-1.5 text-[#EF5350] text-sm font-semibold"><AlertTriangle size={16} /> It is not known whether this message was sent</div>
          <div className="text-[#B0BEC5] text-sm mt-1">
            It was handed to Gmail on {fmtDateTime(e.send_started_at)} and no clear answer came back{e.send_error ? ` (${e.send_error})` : ''}.
            To avoid sending it twice, it will not be sent again until the administrator has looked in the mailbox’s Sent folder.
          </div>
          {e.rfc822_message_id && <div className="text-[#546E7A] text-xs mt-2 break-all">To find it in Gmail, search for: rfc822msgid:{e.rfc822_message_id.replace(/[<>]/g, '')}</div>}
          {isAdmin ? (
            <div className="mt-3">
              <label className={label}>What did you find in the Sent folder? (recorded)</label>
              <input className={input} value={resolveNote} maxLength={500} onChange={(x) => setResolveNote(x.target.value)} placeholder="e.g. It is in Sent, dated today" />
              <div className="flex flex-wrap gap-2 mt-3">
                <button className={btnGhost} disabled={!!busy || resolveNote.trim().length < 5} onClick={() => resolve('sent')}>It was sent</button>
                <button className={btnGhost} disabled={!!busy || resolveNote.trim().length < 5} onClick={() => resolve('not_sent')}>It was not sent (allow sending again)</button>
              </div>
            </div>
          ) : <div className="text-[#B0BEC5] text-sm mt-2">Ask the administrator to check.</div>}
        </div>
      )}

      {/* ── Imported history: a record, shown as the spreadsheet had it ── */}
      {e.source === 'import' && (
        <div className={`${card} border-[#B39DDB]/40`}>
          <div className="text-[#B39DDB] text-sm font-semibold">
            {e.status === 'imported' ? 'Sent · imported record' : 'Imported record · sending not confirmed'}
          </div>
          <div className="text-[#B0BEC5] text-sm mt-1">
            {e.status === 'imported'
              ? 'This email was sent before the Recruiting Center existed. It was not sent through SchemeSync.'
              : 'This was in the spreadsheet with nothing to show it was sent. It does not count as contact with the college.'}
            {' '}It is a record only and cannot be edited, approved or sent.
          </div>
          <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm mt-3">
            <dt className="text-[#546E7A]">Sent</dt>
            <dd className="text-white">{e.status === 'imported_unverified' && !e.sent_at ? unknown : fmtSent(e)}
              {e.sent_at && e.sent_precision === 'date' && <span className="text-[#546E7A] text-xs"> · time of day not recorded</span>}
              {e.sent_original && <div className="text-[#546E7A] text-xs">In the spreadsheet: “{e.sent_original}”</div>}
            </dd>
            <dt className="text-[#546E7A]">Coach</dt><dd className="text-white">{e.to_name || unknown}{e.to_role ? <span className="text-[#B0BEC5]"> · {e.to_role}</span> : null}</dd>
            <dt className="text-[#546E7A]">To</dt><dd className="text-[#B0BEC5] break-all">{e.to_email}</dd>
            <dt className="text-[#546E7A]">CC</dt><dd className="text-[#B0BEC5] break-words">{e.cc_original || <span className="text-[#546E7A]">none recorded</span>}</dd>
            {e.from_original && (<><dt className="text-[#546E7A]">From</dt><dd className="text-[#B0BEC5] break-all">{e.from_original}</dd></>)}
            <dt className="text-[#546E7A]">Source</dt><dd className="text-[#B0BEC5] break-words">{e.import_ref || 'Logged by hand'}</dd>
          </dl>
          <div className="border-t border-[#546E7A]/25 mt-3 pt-3">
            <div className="text-[#546E7A] text-xs">Subject</div>
            <div className="text-white font-semibold mb-2">{e.subject || unknown}</div>
            <div className="text-[#546E7A] text-xs">Message, as recorded</div>
            <div className="text-[#B0BEC5] text-sm whitespace-pre-wrap break-words">{e.body || unknown}</div>
          </div>
          {e.import_extra && Object.keys(e.import_extra).length > 0 && (
            <div className="border-t border-[#546E7A]/25 mt-3 pt-3 text-xs text-[#546E7A]">
              <div className="mb-1">Other details kept with this record</div>
              {Object.entries(e.import_extra).filter(([k]) => !k.startsWith('Spreadsheet draft')).map(([k, v]) => <div key={k} className="[overflow-wrap:anywhere]"><span className="text-[#B0BEC5]">{k}:</span> {String(v)}</div>)}
              {(e.import_extra['Spreadsheet draft body'] || e.import_extra['Spreadsheet draft subject']) && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[#B0BEC5]">What the spreadsheet had prepared (before the sent message was found)</summary>
                  {e.import_extra['Spreadsheet draft subject'] && <div className="mt-1.5 text-[#B0BEC5] font-semibold [overflow-wrap:anywhere]">{e.import_extra['Spreadsheet draft subject']}</div>}
                  {e.import_extra['Spreadsheet draft body'] && <div className="mt-1.5 whitespace-pre-wrap [overflow-wrap:anywhere]">{e.import_extra['Spreadsheet draft body']}</div>}
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {e.source !== 'import' && ['sent', 'cancelled'].includes(e.status) && (
        <div className={`${card} ${e.status === 'sent' ? 'border-[#00E676]/40' : ''}`}>
          {e.status === 'sent' && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 text-[#00E676] text-sm font-semibold"><CheckCircle2 size={16} />
                {e.delivery_status === 'confirmed_in_mailbox' ? 'Sent · confirmed by the administrator in the mailbox' : 'Sent'} · {fmtDateTime(e.sent_at)}
              </div>
              <div className="text-[#B0BEC5] text-sm mt-1">
                Sent by {e.sent_by_name || 'unknown'}. Gmail has taken this message for delivery.
                That does not confirm it reached the coach’s inbox, and it does not show whether it was opened.
              </div>
              <div className="text-[#546E7A] text-xs mt-1 break-all">Gmail message ID: {e.provider_message_id}</div>
            </div>
          )}
          <div className="text-[#546E7A] text-xs mb-2">{e.status === 'sent' ? '' : 'Discarded. Never sent.'}</div>
          {e.subject && <div className="text-white font-semibold mb-2">{e.subject}</div>}
          <div className="text-[#B0BEC5] text-sm whitespace-pre-wrap">{e.body || 'No message text was recorded.'}</div>
        </div>
      )}

      {e.answers && isDraft && (
        <div className={`${card} border-[#546E7A]/50`}>
          <div className="text-[#546E7A] text-xs mb-1">You are replying to {e.answers.from_name || e.answers.from_email} · {fmtDateTime(e.answers.received_at)}</div>
          <div className="text-white text-sm font-semibold [overflow-wrap:anywhere]">{e.answers.subject}</div>
          <div className="text-[#B0BEC5] text-sm whitespace-pre-wrap [overflow-wrap:anywhere] mt-1.5">{e.answers.body}</div>
        </div>
      )}

      {/* ── Compose: write it, then Send ── */}
      {isDraft && (
        <>
          {!e.answers && e.has_template && (
            <div className={`${card} flex flex-wrap items-center gap-3`}>
              <button className={btnGhost} disabled={!!busy} onClick={useTemplate}>
                <FileText size={15} /> {busy === 'template' ? 'Inserting…' : 'Insert standard email'}
              </button>
              <span className="text-[#546E7A] text-xs flex-1 min-w-[12rem]">Puts the standard recruiting subject and message into this draft, word for word. You can still edit it before sending.</span>
            </div>
          )}

          {ruleProblems.length > 0 && !dirty && (
            <div className="rounded-lg border border-[#EF5350]/40 bg-[#EF5350]/10 px-3 py-2.5 text-sm">
              <div className="text-[#EF5350] font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> Fix this before the message can be sent</div>
              <ul className="text-[#B0BEC5] mt-1.5 space-y-1 list-disc pl-5">{ruleProblems.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          <div className={card}>
            <label className={label}>Subject</label>
            <input className={input} value={subject} maxLength={200} onChange={(x) => setSubject(x.target.value)} />
            <label className={`${label} mt-4`}>Message</label>
            <textarea className={`${input} leading-relaxed`} rows={16} value={body} onChange={(x) => setBody(x.target.value)} />
            <div className="text-[#546E7A] text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{body.trim() ? `${body.trim().split(/\s+/).length} words` : 'Empty'}{dirty ? ' · not saved yet' : ''}</span>
              {e.answers && e.sign_off && !body.includes(e.sign_off) && (
                <button type="button" className="text-[#00D4FF] underline" onClick={() => setBody(`${body.replace(/\s+$/, '')}\n\n${e.sign_off}`)}>
                  Add the standard signature and footer
                </button>
              )}
            </div>
            {e.send_error && <div className="text-[#FFB300] text-xs mt-2">The last attempt did not send it: {e.send_error}</div>}
            <div className="flex flex-wrap gap-2 mt-4">
              <button className={btnPrimary} disabled={!!busy || blocked || !subject.trim() || !body.trim() || ['invalid', 'outdated'].includes(e.contact?.email_status)} onClick={send}>
                <Send size={15} /> {busy === 'send' ? 'Sending…' : 'Send'}
              </button>
              <button className={btnGhost} disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
              <button className={btnDanger} disabled={!!busy} onClick={discard}><Trash2 size={14} /> Discard</button>
            </div>
            <div className="text-[#546E7A] text-xs mt-2">
              Send asks you to confirm, then emails this one message. Nothing is sent automatically.
            </div>
          </div>
        </>
      )}

      {e.can_delete && !isDraft && (
        <div className="pt-2 border-t border-[#546E7A]/20">
          <button className={btnDanger} disabled={!!busy} onClick={removeRecord}><Trash2 size={14} /> {busy === 'delete' ? 'Deleting…' : 'Delete this record'}</button>
          <span className="text-[#546E7A] text-xs ml-2">Administrator only. Permanent.</span>
        </div>
      )}
    </div>
  );
};

/* ── Contacts ────────────────────────────────────────────────────────────── */

const EMPTY_CONTACT = {
  college_name: '', division: '', conference: '', state: '', full_name: '', title: '', email: '', phone: '',
  twitter_handle: '', source: '', verified_on: '', notes: '', do_not_contact: false, email_status: 'ok', status_note: '',
};

const ADDRESS = {
  ok: { text: 'Address OK', cls: '' },
  unverified: { text: 'Unverified', cls: 'text-[#FFB300] border-[#FFB300]/40 bg-[#FFB300]/10' },
  outdated: { text: 'Outdated', cls: 'text-[#EF5350] border-[#EF5350]/40 bg-[#EF5350]/12' },
  invalid: { text: 'Invalid address', cls: 'text-[#EF5350] border-[#EF5350]/40 bg-[#EF5350]/12' },
};
const AddressTag = ({ status }) => (status && status !== 'ok'
  ? <span className={`inline-block text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 whitespace-nowrap ${ADDRESS[status].cls}`}>{ADDRESS[status].text}</span> : null);

const ContactForm = ({ api, initial, done, cancel }) => {
  const [f, setF] = useState(initial);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (x) => setF({ ...f, [k]: x.target.type === 'checkbox' ? x.target.checked : x.target.value });
  const corrected = initial.id && f.email.trim().toLowerCase() !== (initial.email || '').toLowerCase();
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true); setError('');
    try {
      const body = { ...f, verified_on: f.verified_on || null, email_status: f.email_status || 'ok' };
      await api(initial.id ? `/contacts/${initial.id}` : '/contacts', { method: initial.id ? 'PUT' : 'POST', body });
      done();
    } catch (x) { setError(x.message); } finally { setBusy(false); }
  };
  const field = (k, text, { wide, ...props } = {}) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className={label}>{text}</label>
      <input className={input} value={f[k] || ''} onChange={set(k)} {...props} />
    </div>
  );
  return (
    <form onSubmit={submit} className={`${card} border-[#00D4FF]/40 space-y-4`}>
      <div className="text-white font-semibold">{initial.id ? 'Edit contact' : 'Add a coach'}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field('college_name', 'College', { required: true, maxLength: 200, wide: true })}
        {field('division', 'Division (e.g. FBS, FCS, D2)', { maxLength: 40 })}
        {field('state', 'State (2 letters)', { maxLength: 2 })}
        {field('full_name', 'Coach’s name (leave blank if unknown)', { maxLength: 200 })}
        {field('title', 'Role (e.g. Offensive Line Coach)', { maxLength: 200 })}
        <div>
          <label className={label}>Email</label>
          <input className={input} type="email" required maxLength={255} value={f.email || ''}
            onChange={(x) => setF({ ...f, email: x.target.value, ...(initial.id && f.email_status !== 'ok' && x.target.value.trim().toLowerCase() !== (initial.email || '').toLowerCase() ? { email_status: 'ok', status_note: '' } : {}) })} />
          {corrected && <div className="text-[#FFB300] text-xs mt-1">Correcting {initial.email}. Earlier messages keep the address they were sent to.</div>}
        </div>
        <div>
          <label className={label}>Address status</label>
          <select className={input} value={f.email_status || 'ok'} onChange={set('email_status')}>
            <option value="ok">OK</option>
            <option value="unverified">Unverified (not confirmed current)</option>
            <option value="outdated">Outdated (coach has left or moved)</option>
            <option value="invalid">Invalid (bad address)</option>
          </select>
        </div>
        {(f.email_status || 'ok') !== 'ok' && field('status_note', 'Why (shown with the contact)', { maxLength: 1000, wide: true })}
        {field('phone', 'Phone', { maxLength: 40 })}
        {field('source', 'Where this came from (e.g. staff directory)', { maxLength: 100 })}
        {field('verified_on', 'Date you last verified it', { type: 'date' })}
        <div className="sm:col-span-2">
          <label className={label}>Notes</label>
          <textarea className={input} rows={2} value={f.notes || ''} onChange={set('notes')} maxLength={4000} />
        </div>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-[#B0BEC5]">
          <input type="checkbox" checked={f.do_not_contact} onChange={set('do_not_contact')} />
          Do not contact (asked not to be emailed, or the address is bad)
        </label>
      </div>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex gap-2">
        <button className={btnPrimary} disabled={busy}>{busy ? 'Saving…' : 'Save contact'}</button>
        <button type="button" className={btnGhost} onClick={cancel}>Cancel</button>
      </div>
    </form>
  );
};

const Contacts = ({ api, openEmail, isAdmin }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [logging, setLogging] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api(`/contacts${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`).then(setRows).catch((e) => setError(e.message));
    api('/colleges/prospects').then(setProspects).catch(() => {});
  }, [api, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const act = async (fn) => { setError(''); try { await fn(); } catch (e) { setError(e.message); } };
  const newDraft = (c) => act(async () => openEmail((await api('/emails', { method: 'POST', body: { contact_id: c.id } })).id));
  const remove = (c) => act(async () => {
    if (!window.confirm(`Delete ${c.full_name || c.email} (${c.college_name}) from the directory?\n\nMessages already written to this address are kept. To remove those too, open the conversation from Outreach and delete it.`)) return;
    await api(`/contacts/${c.id}`, { method: 'DELETE' }); load();
  });
  const mark = (c, status) => act(async () => {
    let note = c.status_note || '';
    if (status !== 'ok') {
      note = window.prompt(`Mark ${c.email} as ${status}. Reason (optional):`, note);
      if (note === null) return;
    }
    await api(`/contacts/${c.id}/status`, { method: 'PATCH', body: { email_status: status, status_note: status === 'ok' ? null : note } }); load();
  });
  const logPast = (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    act(async () => {
      await api('/emails/log-past', { method: 'POST', body: { contact_id: logging.id, sent_on: fd.get('sent_on'), subject: fd.get('subject'), notes: fd.get('notes') } });
      setLogging(null); load();
    });
  };

  const colleges = [];
  (rows || []).forEach((c) => {
    let g = colleges.find((x) => x.id === c.college_id);
    if (!g) { g = { id: c.college_id, name: c.college_name, meta: [c.division, c.conference, c.state].filter(Boolean).join(' · '), last: c.college_last_contacted, contacted: c.college_contacted, maybe: c.college_maybe_contacted, notes: c.college_notes, questionnaire: c.questionnaire_url, contacts: [] }; colleges.push(g); }
    g.contacts.push(c);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-3 text-[#546E7A]" />
          <input className={`${input} pl-9`} placeholder="Search by college, coach, role or email" value={q} onChange={(x) => setQ(x.target.value)} />
        </div>
        <button className={btnPrimary} onClick={() => { setEditing(EMPTY_CONTACT); setLogging(null); }}><Plus size={15} /> Add a coach</button>
      </div>
      <ErrorNote>{error}</ErrorNote>
      {editing && <ContactForm api={api} initial={editing} cancel={() => setEditing(null)} done={() => { setEditing(null); load(); }} />}
      {logging && (
        <form onSubmit={logPast} className={`${card} border-[#00D4FF]/40 space-y-3`}>
          <div className="text-white font-semibold">Log an email already sent to {logging.full_name}</div>
          <div className="text-[#B0BEC5] text-sm">For messages sent outside this page, so {logging.college_name} counts as contacted.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={label}>Date sent</label><input className={input} type="date" name="sent_on" required max={new Date().toISOString().slice(0, 10)} /></div>
            <div><label className={label}>Subject (optional)</label><input className={input} name="subject" maxLength={200} /></div>
            <div className="sm:col-span-2"><label className={label}>Notes (optional)</label><textarea className={input} name="notes" rows={2} maxLength={4000} /></div>
          </div>
          <div className="flex gap-2"><button className={btnPrimary}>Log it</button><button type="button" className={btnGhost} onClick={() => setLogging(null)}>Cancel</button></div>
        </form>
      )}
      {!rows ? <div className="text-[#546E7A] text-sm">Loading…</div>
        : colleges.length === 0 ? (
          <div className={`${card} text-sm text-[#B0BEC5]`}>{q ? 'No coach matches that search.' : 'No coaches in the directory yet. Add the first one above.'}</div>
        ) : colleges.map((g) => (
          <section key={g.id} className={card}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
              <h3 className="text-white font-semibold">{g.name}</h3>
              {g.meta && <span className="text-[#546E7A] text-xs">{g.meta}</span>}
              {g.contacted
                ? <span className="text-[11px] font-bold text-[#00E676] border border-[#00E676]/35 bg-[#00E676]/12 rounded px-1.5 py-0.5">CONTACTED{g.last ? ` ${fmtCentralDate(g.last).toUpperCase()}` : ' · DATE UNKNOWN'}</span>
                : g.maybe
                  ? <span className="text-[11px] font-bold text-[#B0BEC5] border border-[#546E7A]/50 rounded px-1.5 py-0.5">MAY HAVE BEEN CONTACTED · NOT CONFIRMED</span>
                  : <span className="text-[11px] text-[#546E7A]">not contacted yet</span>}
            </div>
            {g.questionnaire && <a href={g.questionnaire} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#00D4FF] text-xs underline -mt-1.5 mb-3">Recruiting questionnaire <ExternalLink size={11} /></a>}
            {g.notes && <div className="text-[#B0BEC5] text-xs -mt-1.5 mb-3 whitespace-pre-wrap [overflow-wrap:anywhere]">{g.notes}</div>}
            <div className="divide-y divide-[#546E7A]/20">
              {g.contacts.map((c) => (
                <div key={c.id} className="py-3 first:pt-0 last:pb-0 flex flex-col md:flex-row md:items-start gap-3">
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="text-white font-medium">{c.full_name || <span className="text-[#FFB300] font-normal">Name unknown</span>}{c.title ? <span className="text-[#B0BEC5] font-normal"> · {c.title}</span> : null}</div>
                    <div className="text-[#B0BEC5] break-all"><span className={['invalid', 'outdated'].includes(c.email_status) ? 'line-through decoration-[#EF5350]/70' : ''}>{c.email}</span>{c.phone ? ` · ${c.phone}` : ''}</div>
                    {(c.email_status !== 'ok' || c.delivery_failed) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <AddressTag status={c.email_status} />
                        {c.delivery_failed && <span className="inline-block text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 text-[#EF5350] border-[#EF5350]/40 bg-[#EF5350]/12">Failed</span>}
                        {c.status_note && <span className="text-[#B0BEC5] text-xs [overflow-wrap:anywhere]">{c.status_note}</span>}
                      </div>
                    )}
                    <div className="text-[#546E7A] text-xs mt-0.5 [overflow-wrap:anywhere]">
                      {[c.source && `Source: ${c.source}`, c.verified_on ? `Verified ${fmtDate(`${c.verified_on}T12:00:00`)}` : 'Not verified'].filter(Boolean).join(' · ')}
                    </div>
                    {c.notes && <div className="text-[#B0BEC5] text-xs mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{c.notes}</div>}
                    {c.do_not_contact && <div className="text-[#EF5350] text-xs font-bold mt-1 inline-flex items-center gap-1"><Ban size={12} /> DO NOT CONTACT</div>}
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <button className={btnPrimary} disabled={c.do_not_contact || ['invalid', 'outdated'].includes(c.email_status)} title={['invalid', 'outdated'].includes(c.email_status) ? 'Correct the address first' : ''} onClick={() => newDraft(c)}><Mail size={14} /> Write email</button>
                    <select aria-label="Address status" className="bg-[#0A1628] text-[#B0BEC5] text-sm rounded-lg border border-[#546E7A]/50 px-2 py-2" value={c.email_status} onChange={(x) => mark(c, x.target.value)}>
                      <option value="ok">Address OK</option><option value="unverified">Unverified</option><option value="outdated">Outdated</option><option value="invalid">Invalid</option>
                    </select>
                    <button className={btnGhost} onClick={() => { setLogging(c); setEditing(null); }}>Log sent email</button>
                    <button className={btnGhost} aria-label="Edit contact" onClick={() => { setEditing({ ...EMPTY_CONTACT, ...Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v ?? ''])), do_not_contact: c.do_not_contact, email_status: c.email_status || 'ok' }); setLogging(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}><Pencil size={14} /> Edit</button>
                    {isAdmin && <button className={btnDanger} aria-label="Delete contact" onClick={() => remove(c)}><Trash2 size={14} /> Delete</button>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      {/* Colleges on the list that have nobody to write to yet. */}
      {(() => {
        const list = prospects.filter((g) => !q.trim() || g.name.toLowerCase().includes(q.trim().toLowerCase()));
        return list.length > 0 && (
          <section className={card}>
            <div className="flex flex-wrap items-baseline gap-x-3 mb-1">
              <h3 className="text-white font-semibold">Prospect colleges</h3>
              <span className="text-[#546E7A] text-sm">{list.length}</span>
            </div>
            <div className="text-[#546E7A] text-xs mb-3">On the list, with no coach email address yet. None of these has been contacted. Add a coach above to start writing to one.</div>
            <div className="divide-y divide-[#546E7A]/20">
              {list.map((g) => (
                <div key={g.id} className="py-2 first:pt-0 last:pb-0 text-sm">
                  <span className="text-white font-medium">{g.name}</span>
                  <span className="text-[#546E7A] text-xs"> {[g.division, g.conference, g.state].filter(Boolean).join(' · ')}</span>
                  {g.notes && <div className="text-[#B0BEC5] text-xs mt-0.5 [overflow-wrap:anywhere]">{g.notes}</div>}
                </div>
              ))}
            </div>
          </section>
        );
      })()}
    </div>
  );
};

/* ── Settings ────────────────────────────────────────────────────────────── */

const MailboxCard = ({ api, openEmail }) => {
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => authFetch(`${API}/api/recruiting/gmail/status`).then((r) => r.json()).then(setM).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!m) return null;
  const act = async (fn) => { setBusy(true); setError(''); try { await fn(); } catch (x) { setError(x.message); } finally { setBusy(false); } };
  const connect = () => act(async () => {
    const r = await authFetch(`${API}/api/recruiting/gmail/connect`, { method: 'POST' });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Could not start the connection');
    window.location.href = d.url;
  });
  const disconnect = () => act(async () => {
    if (!window.confirm('Disconnect the recruiting mailbox? Nothing can be sent until it is connected again.')) return;
    await authFetch(`${API}/api/recruiting/gmail/disconnect`, { method: 'POST' });
    load();
  });
  const test = () => act(async () => openEmail((await api('/emails/test-message', { method: 'POST' })).id));
  return (
    <div className={`${card} space-y-3`}>
      <div className="text-white font-semibold">Recruiting mailbox <span className="text-[#546E7A] font-normal text-sm">· administrator only</span></div>
      <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm">
        <dt className="text-[#546E7A]">Messages are sent from</dt><dd className="text-white break-all">{m.sender_email}</dd>
        <dt className="text-[#546E7A]">Connection</dt>
        <dd className={m.connected ? 'text-[#00E676]' : 'text-[#FFB300]'}>
          {m.connected ? `Connected as ${m.connected_as} since ${fmtDate(m.connected_at)}`
            : m.configured ? 'Not connected' : 'Not set up on the server yet'}
        </dd>
        <dt className="text-[#546E7A]">Sending to coaches</dt>
        <dd className={m.coach_sending_enabled ? 'text-[#00E676]' : 'text-[#FFB300]'}>{m.coach_sending_enabled ? 'On' : 'Off (only a test message to the test address can be sent)'}</dd>
        <dt className="text-[#546E7A]">Test address</dt><dd className="text-[#B0BEC5] break-all">{m.test_recipient || 'not set'}</dd>
      </dl>
      {m.last_error && <div className="text-[#FFB300] text-xs">Last problem: {m.last_error}</div>}
      <ErrorNote>{error}</ErrorNote>
      <div className="flex flex-wrap gap-2">
        {!m.connected && <button type="button" className={btnPrimary} disabled={busy || !m.configured} onClick={connect}>Connect the recruiting mailbox</button>}
        {m.connected && <button type="button" className={btnPrimary} disabled={busy || !m.test_recipient} onClick={test}>Create a test message</button>}
        {m.connected && <button type="button" className={btnGhost} disabled={busy} onClick={disconnect}>Disconnect</button>}
      </div>
      <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-2 text-sm">
        <dt className="text-[#546E7A]">Inbox (reading)</dt>
        <dd className={m.inbox_enabled ? 'text-[#00E676]' : 'text-[#FFB300]'}>{m.inbox_enabled ? 'On, read-only' : m.connected ? 'Off: connect again from the Inbox tab to allow reading' : 'Off'}</dd>
      </dl>
      <div className="text-[#546E7A] text-xs">The connection sends mail as this mailbox and, once allowed, reads it. It cannot delete, move or change anything in the mailbox. Turning on sending to coaches is done on the server, not here.</div>
    </div>
  );
};

const AthleteLogin = ({ api }) => {
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ username: '', password: '', again: '' });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('/athlete-login').then((r) => { setSt(r); setF((x) => ({ ...x, username: r.username || '' })); }).catch((e) => setError(e.message)); }, [api]);
  const min = st?.password_min || 8;
  // Checked here and said out loud, so a problem is never a silent refusal.
  const save = async () => {
    setError(''); setMsg('');
    const name = f.username.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9._-]{2,39}$/.test(name)) { setError('The username must be 3 to 40 characters: letters, numbers, dots, dashes or underscores. No spaces and no @.'); return; }
    if (f.password.length < min) { setError(`The password must be at least ${min} characters.`); return; }
    if (f.password !== f.again) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      const r = await api('/athlete-login', { method: 'POST', body: { username: name, password: f.password } });
      setSt(r); setF({ username: r.username, password: '', again: '' });
      setMsg(r.created ? `Login created. Username: ${r.username}. It works now on the athlete sign-in page below.` : `Saved. Username: ${r.username}. The new password works now.`);
    } catch (x) { setError(x.message); }
    setBusy(false);
  };
  return (
    <div className={`${card} space-y-3`} onKeyDown={(x) => { if (x.key === 'Enter') { x.preventDefault(); save(); } }}>
      <div className="text-white font-semibold">Athlete’s login <span className="text-[#546E7A] font-normal text-sm">· administrator only</span></div>
      <div className="text-[#B0BEC5] text-sm">
        {st?.exists ? <>A login exists. Username: <b className="text-white [overflow-wrap:anywhere]">{st.username}</b>. Enter a new password to reset it.</> : <b className="text-[#FFB300]">No login exists yet. Choose a username and a first password.</b>}
        {' '}The athlete signs in with the username and password, and can compose, send, reply and follow outreach. Settings, imports, deleting and logins stay with you.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className={label}>Username</label><input className={input} type="text" maxLength={40} value={f.username} onChange={(x) => setF({ ...f, username: x.target.value })} autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="e.g. channing" /></div>
        <div><label className={label}>Password ({min}+ characters)</label><input className={input} type="password" value={f.password} onChange={(x) => setF({ ...f, password: x.target.value })} autoComplete="new-password" /></div>
        <div><label className={label}>Password again</label><input className={input} type="password" value={f.again} onChange={(x) => setF({ ...f, again: x.target.value })} autoComplete="new-password" /></div>
      </div>
      <ErrorNote>{error}</ErrorNote>
      {msg && <div role="status" className="rounded-lg border border-[#00E676]/50 bg-[#00E676]/10 px-3 py-2 text-[#00E676] text-sm font-semibold [overflow-wrap:anywhere]">{msg}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={btnPrimary} disabled={busy} onClick={save}>{busy ? 'Saving…' : st?.exists ? 'Save login' : 'Create login'}</button>
        <span className="text-[#546E7A] text-xs [overflow-wrap:anywhere]">Athlete sign-in address: {window.location.origin}/recruiting-login</span>
      </div>
    </div>
  );
};

const SettingsTab = ({ api, isAdmin, openEmail }) => {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    api('/settings').then((r) => setS({ ...r, approve_personal: !!r.personal_approved_at })).catch((e) => setError(e.message));
  }, [api]);
  if (!s) return error ? <ErrorNote>{error}</ErrorNote> : <div className="text-[#546E7A] text-sm">Loading…</div>;
  const hasPersonal = !!(s.values_notes || s.voice_notes || s.writing_samples);
  const set = (k) => (x) => { setS({ ...s, [k]: x.target.value }); setSaved(''); };
  const save = async (ev) => {
    ev.preventDefault(); setError(''); setSaved('');
    try {
      setS(await api('/settings', {
        method: 'PUT',
        body: { ...s, approve_personal: isAdmin ? !!s.approve_personal : undefined },
      }).then((r) => ({ ...r, approve_personal: !!r.personal_approved_at })));
      setSaved('Saved');
    } catch (x) { setError(x.message); }
  };
  const area = (k, title, help, rows, placeholder) => (
    <div>
      <label className="block text-white text-sm font-semibold">{title}</label>
      <div className="text-[#546E7A] text-xs mb-1.5">{help}</div>
      <textarea className={`${input} leading-relaxed`} rows={rows} value={s[k] || ''} onChange={set(k)} placeholder={placeholder} />
    </div>
  );
  return (
    <div className="space-y-5 max-w-3xl">
    <form onSubmit={save} className="space-y-5">
      <div className={`${card} space-y-4`}>
        <div>
          <div className="text-white font-semibold">How you are identified</div>
          <div className="text-[#B0BEC5] text-sm mt-1">Every draft uses this wording for your position and school, and ends with this identification. It is set by the administrator.</div>
        </div>
        {isAdmin ? (
          <>
            <div><label className={label}>Position, exactly as drafts must say it</label><input className={input} value={s.position_statement || ''} onChange={set('position_statement')} maxLength={300} /></div>
            <div><label className={label}>School, exactly as it must always be written</label><input className={input} value={s.school_display || ''} onChange={set('school_display')} maxLength={200} /></div>
            <div><label className={label}>Standard identification (ends every draft)</label><textarea className={input} rows={5} value={s.standard_identification || ''} onChange={set('standard_identification')} maxLength={600} /></div>
          </>
        ) : (
          <div className="rounded-lg bg-[#0A1628] border border-[#546E7A]/30 p-3.5 text-sm text-white whitespace-pre-wrap">
            {s.standard_identification || 'Not set yet.'}
          </div>
        )}
      </div>
      <div className={`${card} space-y-5`}>
        <div>
          <div className="text-white font-semibold">About you <span className="text-[#546E7A] font-normal text-sm">· optional</span></div>
          <div className="text-[#B0BEC5] text-sm mt-1">Drafts work without any of this: they are written from your verified recruiting profile. If you add something here, drafts may use it once the administrator has approved it. Nothing about your faith, character or personal life is ever written for you.</div>
          <div className={`text-xs font-semibold mt-2 ${s.personal_approved_at ? 'text-[#00E676]' : 'text-[#FFB300]'}`}>
            {!hasPersonal ? '' : s.personal_approved_at
              ? `Approved by ${s.personal_approved_by_name || 'the administrator'} on ${fmtDate(s.personal_approved_at)}. Drafts may use it.`
              : 'Not approved yet. Drafts will not use it until the administrator approves it.'}
          </div>
        </div>
        {area('values_notes', 'Character, faith, what you value', 'In your own words.', 5, '')}
        {area('voice_notes', 'How you write', 'Formal or relaxed? Short sentences? Words you would never use?', 3, '')}
        {area('writing_samples', 'Something you have written', 'A past email or a paragraph you wrote, so drafts can match how you sound.', 5, '')}
        {isAdmin && (
          <label className="flex items-start gap-2 text-sm text-white">
            <input type="checkbox" className="mt-1" disabled={!hasPersonal} checked={!!s.approve_personal && hasPersonal}
              onChange={(x) => { setS({ ...s, approve_personal: x.target.checked }); setSaved(''); }} />
            <span>I have read these notes and approve them for use in drafts.</span>
          </label>
        )}
        {area('signature', 'Extra lines under your identification', 'Optional, e.g. a phone number. Your standard identification already ends every draft.', 2, '')}
      </div>
      <div className={`${card} space-y-3`}>
        <div className="text-white font-semibold">Copies</div>
        <div>
          <label className={label}>Copy every message to (optional)</label>
          <input className={input} type="email" value={s.cc_email || ''} onChange={set('cc_email')} placeholder="A parent’s email address" />
          <div className="text-[#546E7A] text-xs mt-1.5">The copy address is fixed into each message when it is approved, and shown before you approve and before you send.</div>
        </div>
      </div>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex items-center gap-3">
        <button className={btnPrimary}>Save settings</button>
        {saved && <span className="text-[#00E676] text-sm font-semibold">{saved}</span>}
      </div>
    </form>
    {/* Separate from the form above: a form inside a form submits the outer one. */}
    {isAdmin && <AthleteLogin api={api} />}
    {isAdmin && <MailboxCard api={api} openEmail={openEmail} />}
    </div>
  );
};

/* ── Activity ────────────────────────────────────────────────────────────── */

// Plain sentences for the things people do here. `t` is who or what it was about.
const PLAIN = {
  'email.draft_created': (t) => `started a message to ${t}`,
  'email.reply_started': (t) => `started a reply to ${t}`,
  'email.approved': (t) => `approved a message to ${t}`,
  'email.sent': (t) => `sent a message to ${t}`,
  'email.send_failed': (t) => `tried to send to ${t}, but it was not sent`,
  'email.cancelled': (t) => `discarded a draft to ${t}`,
  'email.past_logged': (t) => `logged an earlier email to ${t}`,
  'contact.created': (t) => `added ${t} to Contacts`,
  'contact.updated': (t, d) => (d.email_corrected ? `corrected the address for ${t} (was ${d.email_was})` : d.email_status ? `marked ${t} as ${d.email_status}` : `updated ${t}`),
  'contact.status_changed': (t, d) => (d.email_status === 'ok' ? `marked the address for ${t} as OK` : `marked ${t} as ${d.email_status}`),
  'contact.deleted': (t) => `deleted ${t} from Contacts`,
  'record.deleted': (t, d) => `deleted a ${d.test ? 'test message' : d.status === 'sent' ? 'sent-message record' : d.status?.startsWith('imported') ? 'previously-sent record' : 'draft'} for ${t}`,
  'conversation.deleted': (t, d) => `deleted the conversation with ${d.email} (${d.messages_removed + d.received_removed} items)`,
  'inbox.deleted': (t, d) => `deleted a received message from ${d.from_email}`,
  'questionnaire.completed': (t, d) => `completed the ${d.school} recruiting questionnaire`,
  'campaign.started': (t, d) => `started the email campaign (${d.colleges} colleges, ${d.per_batch || d.per_day} ${d.per_batch ? 'per batch' : 'a day'})`,
  'campaign.paused': () => 'paused the email campaign',
  'campaign.resumed': () => 'resumed the email campaign',
  'campaign.stopped': () => 'stopped the email campaign',
  'discovery.added': (t, d) => `added ${d.colleges_added} college(s) and ${d.contacts_added} contact(s) from Discover${d.colleges?.length ? `: ${d.colleges.slice(0, 5).join(', ')}${d.colleges.length > 5 ? '…' : ''}` : ''}`,
};
const about = (a) => {
  const d = a.detail || {};
  const who = a.who || d.to_name || d.name || a.address || d.to_email || d.email || 'a coach';
  const where = a.college || d.to_school || d.college;
  return where && where !== who ? `${who} (${where})` : who;
};
const plainLine = (a) => {
  const d = a.detail || {};
  if (a.action === 'inbox.received') {
    const n = d.new - (d.bounces || 0);
    return { system: true, text: [n > 0 && `${n} new message${n > 1 ? 's' : ''} arrived in the inbox`, d.bounces > 0 && `${d.bounces} delivery failure${d.bounces > 1 ? 's' : ''} reported`].filter(Boolean).join(' · ') };
  }
  return { text: (PLAIN[a.action] || (() => a.action))(about(a), d) };
};

const Activity = ({ api, isAdmin }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [everything, setEverything] = useState(false);
  useEffect(() => { setRows(null); api(`/audit?limit=200${everything ? '&everything=true' : ''}`).then(setRows).catch((e) => setError(e.message)); }, [api, everything]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="text-[#546E7A] text-xs flex-1 min-w-[12rem]">{everything ? 'The full technical record: sign-ins, settings, imports, tests and each step of sending.' : 'What has been done in recruiting, newest first.'}</div>
        {isAdmin && <button className="text-[#00D4FF] text-xs underline" onClick={() => setEverything(!everything)}>{everything ? 'Show recruiting activity only' : 'Show the full technical record'}</button>}
      </div>
      {!rows ? <div className="text-[#546E7A] text-sm">Loading…</div>
        : rows.length === 0 ? <div className={`${card} text-sm text-[#B0BEC5]`}>Nothing yet. Messages written, approved and sent, replies received and changes to contacts will appear here.</div> : (
          <div className={`${card} divide-y divide-[#546E7A]/20`}>
            {rows.map((a) => {
              const line = everything ? null : plainLine(a);
              return (
                <div key={a.id} className="py-2.5 first:pt-0 last:pb-0 text-sm flex flex-col sm:flex-row sm:items-baseline gap-x-3">
                  <span className="text-[#546E7A] text-xs sm:w-32 flex-shrink-0">{fmtDateTime(a.occurred_at)}</span>
                  {everything ? (
                    <span className="min-w-0 [overflow-wrap:anywhere]"><span className="text-white">{a.actor_name || a.actor_email}</span> <span className="text-[#B0BEC5]">{a.action}{a.entity_id ? ` #${a.entity_id}` : ''}</span>
                      {a.detail && <span className="text-[#546E7A] text-xs"> {JSON.stringify(a.detail).slice(0, 300)}</span>}</span>
                  ) : (
                    <span className="min-w-0 [overflow-wrap:anywhere] text-[#B0BEC5]">{!line.system && <span className="text-white font-medium">{a.actor_name || a.actor_email} </span>}{line.text}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

/* ── Outreach tracking ───────────────────────────────────────────────────── */

const REPLY = {
  replied: { text: 'Replied', cls: 'text-[#00E676] border-[#00E676]/40 bg-[#00E676]/12' },
  awaiting: { text: 'Sent', cls: 'text-[#00D4FF] border-[#00D4FF]/40 bg-[#00D4FF]/10' },
  bounced: { text: 'Failed', cls: 'text-[#EF5350] border-[#EF5350]/40 bg-[#EF5350]/12' },
  auto_reply: { text: 'Automatic reply only', cls: 'text-[#B0BEC5] border-[#546E7A]/50' },
  not_contacted: { text: 'Not contacted', cls: 'text-[#546E7A] border-[#546E7A]/40' },
};
const FOLLOW = { sent: 'Followed up', approved: 'Follow-up approved, not sent', drafted: 'Follow-up drafted', none: '—' };
const Tag = ({ cls, children }) => <span className={`inline-block text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 whitespace-nowrap ${cls}`}>{children}</span>;
const contactedText = (r) => {
  if (!r.contacted) return '—';
  const when = r.date_unknown ? 'date unknown' : fmtCentralDate(r.times_contacted > 1 ? r.last_contacted : r.first_contacted);
  return `${when}${r.only_as_copy ? ' · as a copy' : ''}${r.times_contacted > 1 ? ` · ${r.times_contacted} messages` : ''}`;
};

const Outreach = ({ api, openTalk }) => {
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  useEffect(() => { api('/tracking').then(setT).catch((e) => setError(e.message)); }, [api]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!t) return <div className="text-[#546E7A] text-sm">Loading…</div>;
  const filters = [
    ['all', `All ${t.totals.coaches}`], ['replied', `Replied ${t.totals.replied}`], ['awaiting', `Sent ${t.totals.awaiting}`],
    ['bounced', `Failed ${t.totals.bounced}`], ['not_contacted', `Not contacted ${t.totals.coaches - t.totals.contacted}`],
  ];
  const needle = q.trim().toLowerCase();
  const rows = t.rows.filter((r) => (filter === 'all' || r.reply_status === filter)
    && (!needle || `${r.college_name} ${r.coach_name || ''} ${r.email}`.toLowerCase().includes(needle)));
  const others = (id) => t.other_staff_replies.filter((o) => o.college_id === id);
  let lastCollege = null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[[t.totals.colleges_contacted, 'Colleges contacted', `${t.totals.contacted} coaches`], [t.totals.colleges_replied, 'Colleges replied', `${t.totals.replied} coaches`],
          [t.totals.bounced, 'Failed', 'a delivery failure came back'], [t.totals.awaiting, 'Sent', 'no reply or failure yet']].map(([n, a, b]) => (
          <div key={a} className={card}><div className="text-2xl font-bold text-white">{n}</div><div className="text-sm font-semibold text-[#B0BEC5]">{a}</div><div className="text-[11px] text-[#546E7A] mt-0.5">{b}</div></div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex flex-wrap gap-1.5">
          {filters.map(([k, text]) => (
            <button key={k} onClick={() => setFilter(k)} className={`text-xs rounded px-2 py-1.5 border ${filter === k ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-[#546E7A]/40 text-[#B0BEC5]'}`}>{text}</button>
          ))}
        </div>
        <input className={`${input} sm:max-w-xs sm:ml-auto`} placeholder="Find a college or coach" value={q} onChange={(x) => setQ(x.target.value)} />
      </div>
      <div className={`${card} p-0 overflow-hidden`}>
        <div className="hidden md:grid grid-cols-[1.3fr_1.5fr_1.2fr_1fr_1fr] gap-3 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-[#546E7A] border-b border-[#546E7A]/25">
          <div>College</div><div>Coach</div><div>Contacted</div><div>Reply</div><div>Follow-up</div>
        </div>
        {rows.length === 0 && <div className="px-4 py-3 text-sm text-[#546E7A]">Nobody matches.</div>}
        {rows.map((r) => {
          const first = r.college_id !== lastCollege; lastCollege = r.college_id;
          return (
            <button key={r.contact_id} onClick={() => openTalk(r.email)}
              className={`w-full text-left px-4 py-2.5 grid grid-cols-1 md:grid-cols-[1.3fr_1.5fr_1.2fr_1fr_1fr] gap-x-3 gap-y-1 items-center text-sm ${first ? 'border-t border-[#546E7A]/25' : ''} hover:bg-[#243B55]/40`}>
              <div className={`font-semibold ${first ? 'text-white' : 'text-white md:text-transparent md:select-none'}`}>{r.college_name}
                {first && others(r.college_id).map((o) => (
                  <div key={o.from_email} className="text-[11px] font-normal text-[#00E676] [overflow-wrap:anywhere]">also heard from {o.from_name || o.from_email}{o.new_replies ? ' · NEW' : ''}</div>
                ))}
              </div>
              <div className="min-w-0"><div className="text-[#B0BEC5] truncate">{r.coach_name || 'Name unknown'}{r.do_not_contact ? ' · DO NOT CONTACT' : ''}</div><div className="text-[#546E7A] text-xs truncate">{r.coach_title || r.email}</div><AddressTag status={r.email_status} /></div>
              <div className="text-[#B0BEC5] text-xs md:text-sm"><span className="md:hidden text-[#546E7A]">Contacted: </span>{contactedText(r)}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Tag cls={REPLY[r.reply_status].cls}>{REPLY[r.reply_status].text}</Tag>
                {r.new_replies > 0 && <Tag cls="text-[#0A0E27] bg-[#00E676] border-[#00E676]">New</Tag>}
                {r.last_reply && <span className="text-[11px] text-[#546E7A]">{fmtDate(r.last_reply)}</span>}
              </div>
              <div className="text-[#B0BEC5] text-xs md:text-sm"><span className="md:hidden text-[#546E7A]">Follow-up: </span>{FOLLOW[r.follow_up]}</div>
            </button>
          );
        })}
      </div>
      <div className="text-[#546E7A] text-xs">Click a line to see the whole conversation. Dates of earlier outreach are shown in Central Time. “Sent” means the message went out, not that it arrived: only a reply or a failure notice says what happened.</div>
    </div>
  );
};

/* ── Inbox ───────────────────────────────────────────────────────────────── */

const KIND = { bounce: ['Delivery failure', 'text-[#EF5350] border-[#EF5350]/40'], auto_reply: ['Automatic reply', 'text-[#B0BEC5] border-[#546E7A]/50'] };

const InboxTab = ({ api, openTalk, isAdmin }) => {
  const [box, setBox] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api('/inbox').then(setBox).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); }, [load]);
  const check = async () => {
    setBusy(true); setError('');
    try { await api('/inbox/sync', { method: 'POST' }); } catch (e) { setError(e.message); }
    await load(); setBusy(false);
  };
  const reconnect = async () => {
    setError('');
    try {
      const r = await authFetch(`${API}/api/recruiting/gmail/connect`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not start');
      window.location.href = d.url;
    } catch (e) { setError(e.message); }
  };
  if (!box) return error ? <ErrorNote>{error}</ErrorNote> : <div className="text-[#546E7A] text-sm">Loading…</div>;
  const st = box.state;
  return (
    <div className="space-y-4">
      {!st.enabled && (
        <div className={`${card} border-[#FFB300]/50`}>
          <div className="text-[#FFB300] font-semibold text-sm">The inbox is not switched on yet</div>
          <div className="text-[#B0BEC5] text-sm mt-1">
            {st.connected
              ? `${st.mailbox} is connected for sending only. To show coaches’ replies here, it has to be connected once more with permission to read mail. The permission is read-only: nothing in the mailbox can be deleted or changed.`
              : `${st.mailbox} is not connected.`}
          </div>
          {isAdmin
            ? <button className={`${btnPrimary} mt-3`} onClick={reconnect}>Connect the mailbox for reading</button>
            : <div className="text-[#546E7A] text-sm mt-2">Ask the administrator to connect it.</div>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button className={btnPrimary} disabled={busy || !st.enabled} onClick={check}><RefreshCw size={14} /> {busy ? 'Checking…' : 'Check for new mail'}</button>
        <span className="text-[#546E7A] text-xs">
          {st.enabled ? `Reading ${st.mailbox} · last checked ${st.last_synced_at ? fmtDateTime(st.last_synced_at) : 'never'}` : ''}
          {st.last_error ? ` · last problem: ${st.last_error}` : ''}
        </span>
      </div>
      <ErrorNote>{error}</ErrorNote>
      {box.messages.length === 0 ? (
        <div className={`${card} text-sm text-[#B0BEC5]`}>No mail yet. Replies from coaches will appear here, matched to the coach, the college and the message they answer.</div>
      ) : (
        <div className="space-y-2">
          {box.messages.map((m) => (
            <button key={m.id} onClick={() => openTalk(m.bounced_address || m.from_email)}
              className={`${card} w-full text-left ${m.is_new ? 'border-[#00E676]/60' : ''}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {m.is_new && <Tag cls="text-[#0A0E27] bg-[#00E676] border-[#00E676]">New</Tag>}
                {KIND[m.kind] && <Tag cls={KIND[m.kind][1]}>{KIND[m.kind][0]}</Tag>}
                <span className={`text-sm ${m.is_new ? 'text-white font-bold' : 'text-white font-semibold'}`}>{m.college_name || 'Not matched to a college'}</span>
                <span className="text-[#B0BEC5] text-sm">{m.coach_name || m.from_name || m.from_email}</span>
                <span className="text-[11px] text-[#546E7A] ml-auto">{fmtDateTime(m.received_at)}</span>
              </div>
              <div className={`text-sm mt-1 [overflow-wrap:anywhere] ${m.is_new ? 'text-white' : 'text-[#B0BEC5]'}`}>{m.subject || '(no subject)'}</div>
              <div className="text-[#546E7A] text-xs mt-0.5 [overflow-wrap:anywhere]">{(m.excerpt || '').replace(/\s+/g, ' ')}</div>
              <div className="text-[#546E7A] text-[11px] mt-1 [overflow-wrap:anywhere]">
                {m.from_email}
                {m.match_method ? ` · matched: ${m.match_method}` : ' · not matched to any coach or college'}
                {m.matched_subject ? ` · answers “${m.matched_subject}”` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── One conversation ────────────────────────────────────────────────────── */

const Conversation = ({ api, address, back, openEmail }) => {
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/conversation?email=${encodeURIComponent(address)}&mark_read=true`).then(setC).catch((e) => setError(e.message)); }, [api, address]);
  if (error) return <div className="space-y-3"><button onClick={back} className="text-[#B0BEC5] text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back</button><ErrorNote>{error}</ErrorNote></div>;
  if (!c) return <div className="text-[#546E7A] text-sm">Loading…</div>;
  const reply = async (inboundId) => {
    setBusy(true); setError('');
    try { openEmail((await api(`/inbox/${inboundId}/reply`, { method: 'POST' })).id); } catch (e) { setError(e.message); setBusy(false); }
  };
  const lastIn = [...c.items].reverse().find((i) => i.direction === 'in' && i.kind !== 'bounce');
  const removeAll = async () => {
    if (!window.confirm(`Delete the whole conversation with ${c.email}?\n\nThis permanently removes ${c.items.length} item(s): every message written to this address and everything received from it. Emails already sent cannot be recalled, and the mailbox itself is not touched. The contact stays in the directory.`)) return;
    setBusy(true); setError('');
    try { await api(`/conversation?email=${encodeURIComponent(c.email)}`, { method: 'DELETE' }); back(); } catch (e) { setError(e.message); setBusy(false); }
  };
  const removeIn = async (i) => {
    if (!window.confirm(`Delete this received message from ${i.from_email}? It is removed from the Recruiting Center only, not from the mailbox.`)) return;
    setBusy(true); setError('');
    try {
      await api(`/inbox/${i.id}`, { method: 'DELETE' });
      setC({ ...c, items: c.items.filter((x) => !(x.direction === 'in' && x.id === i.id)) });
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <button onClick={back} className="text-[#B0BEC5] text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back</button>
      <div className={card}>
        <div className="text-white font-semibold">{c.college ? c.college.name : 'Not matched to a college'}</div>
        {c.college?.questionnaire_url && <a href={c.college.questionnaire_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#00D4FF] text-xs underline">Recruiting questionnaire <ExternalLink size={11} /></a>}
        <div className="text-[#B0BEC5] text-sm [overflow-wrap:anywhere]">{c.contact?.name || 'Name unknown'}{c.contact?.title ? ` · ${c.contact.title}` : ''} · {c.email}</div>
        {c.contact?.do_not_contact && <div className="text-[#EF5350] text-xs font-bold mt-1">DO NOT CONTACT</div>}
        {c.contact && c.contact.email_status !== 'ok' && <div className="mt-1.5 flex flex-wrap items-center gap-1.5"><AddressTag status={c.contact.email_status} />{c.contact.status_note && <span className="text-[#B0BEC5] text-xs">{c.contact.status_note}</span>}</div>}
      </div>
      <ErrorNote>{error}</ErrorNote>
      {c.items.length === 0 && <div className={`${card} text-sm text-[#B0BEC5]`}>Nothing has been sent to or received from this address.</div>}
      {c.items.map((i) => (i.direction === 'out' ? (
        <div key={`o${i.id}`} className="md:ml-12">
          <div className={`${card} border-[#00D4FF]/30`}>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className="text-[#00D4FF] text-xs font-bold uppercase">You wrote{i.is_reply ? ' (reply)' : ''}</span>
              <StatusPill status={i.status} outcome={i.outcome} />
              <span className="text-[11px] text-[#546E7A] ml-auto">{['sent', 'imported'].includes(i.status) ? (i.dated ? (i.source === 'import' ? fmtImported(i.at, i.sent_precision || 'date') : fmtDateTime(i.at)) : 'date unknown') : `not sent · last edited ${fmtDateTime(i.at)}`}</span>
            </div>
            {i.as_copy && <div className="text-[#546E7A] text-xs mb-1">This coach was copied. {i.as_copy}.</div>}
            <div className="text-white text-sm font-semibold [overflow-wrap:anywhere]">{i.subject || <span className="text-[#546E7A] italic font-normal">Subject unknown</span>}</div>
            <div className="text-[#B0BEC5] text-sm whitespace-pre-wrap [overflow-wrap:anywhere] mt-1.5">{i.body || <span className="text-[#546E7A] italic">No text yet</span>}</div>
            <button className={`${btnGhost} mt-3`} onClick={() => openEmail(i.id)}>Open this message</button>
          </div>
        </div>
      ) : (
        <div key={`i${i.id}`} className="md:mr-12">
          <div className={`${card} ${i.kind === 'bounce' ? 'border-[#EF5350]/50' : 'border-[#00E676]/40'}`}>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className={`text-xs font-bold uppercase ${i.kind === 'bounce' ? 'text-[#EF5350]' : 'text-[#00E676]'}`}>
                {i.kind === 'bounce' ? 'Delivery failure' : i.kind === 'auto_reply' ? 'Automatic reply' : `${i.from_name || i.from_email} wrote`}
              </span>
              {i.is_new && <Tag cls="text-[#0A0E27] bg-[#00E676] border-[#00E676]">New</Tag>}
              <span className="text-[11px] text-[#546E7A] ml-auto">{fmtDateTime(i.at)}</span>
            </div>
            <div className="text-white text-sm font-semibold [overflow-wrap:anywhere]">{i.subject || '(no subject)'}</div>
            <div className="text-[#B0BEC5] text-sm whitespace-pre-wrap [overflow-wrap:anywhere] mt-1.5">{i.body}</div>
            <div className="text-[#546E7A] text-[11px] mt-2 [overflow-wrap:anywhere]">
              {i.from_email}{i.cc ? ` · cc ${i.cc}` : ''}{i.match_method ? ` · matched: ${i.match_method}` : ''}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {i.kind !== 'bounce' && <button className={btnPrimary} disabled={busy} onClick={() => reply(i.id)}><Reply size={14} /> Reply</button>}
              {c.can_delete && <button className={btnDanger} disabled={busy} aria-label="Delete this received message" onClick={() => removeIn(i)}><Trash2 size={14} /></button>}
            </div>
          </div>
        </div>
      )))}
      {lastIn && <div className="text-[#546E7A] text-xs">A reply is written as a draft, then reviewed, approved and sent like every other message. Nothing is sent automatically.</div>}
      {c.can_delete && c.items.length > 0 && (
        <div className="pt-2 border-t border-[#546E7A]/20">
          <button className={btnDanger} disabled={busy} onClick={removeAll}><Trash2 size={14} /> Delete this conversation</button>
          <span className="text-[#546E7A] text-xs ml-2">Administrator only. Permanent.</span>
        </div>
      )}
    </div>
  );
};

/* ── Import past outreach (administrator) ────────────────────────────────── */

const CATEGORY = {
  confirmed: { text: 'Sent', cls: 'text-[#B39DDB]' },
  unconfirmed: { text: 'Sending not confirmed', cls: 'text-[#B0BEC5]' },
  contact_only: { text: 'Contact only', cls: 'text-[#00D4FF]' },
  skipped: { text: 'Skipped', cls: 'text-[#FFB300]' },
  unresolved: { text: 'Unresolved', cls: 'text-[#EF5350]' },
};
const FIELD_LABEL = {
  college: 'College', coach_name: 'Coach name', first_name: 'First name', last_name: 'Last name', title: 'Role',
  email: 'Coach email', cc: 'CC', from: 'From', subject: 'Subject', body: 'Message', sent_at: 'Date sent',
  sent_time: 'Time sent', status: 'Sent status', division: 'Division', conference: 'Conference', state: 'State',
  phone: 'Phone', twitter: 'Twitter / X', notes: 'Notes',
};

// Lets the server read one mailbox's sent recruiting mail, once, to establish
// what was really sent. The mailbox's owner signs in at Google and agrees.
const SentMailFetch = ({ defaultMailbox, defaultPhrase }) => {
  const day = (offset) => new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ mailbox: defaultMailbox || '', phrase: defaultPhrase || '', since: day(-21), until: day(2) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (x) => setF({ ...f, [k]: x.target.value });
  const go = async (ev) => {
    ev.preventDefault(); setBusy(true); setError('');
    try {
      const r = await authFetch(`${API}/api/recruiting/history/connect`, { method: 'POST', body: JSON.stringify(f) });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not start');
      window.location.href = d.url;
    } catch (x) { setError(x.message); setBusy(false); }
  };
  return (
    <form onSubmit={go} className={`${card} space-y-3`}>
      <div className="text-white font-semibold">Confirm what was actually sent</div>
      <div className="text-[#B0BEC5] text-sm">
        Reads the sent mail of one of your own mailboxes for messages that mention the phrase below, so imported
        records can be matched to email that really went out. You sign in at Google as that mailbox and agree.
        The access is read-only, used once for this search, and withdrawn straight away. Nothing is sent or changed
        in the mailbox.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={label}>Mailbox the emails were sent from</label><input className={input} type="email" required value={f.mailbox} onChange={set('mailbox')} /></div>
        <div><label className={label}>Messages mentioning</label><input className={input} required minLength={3} value={f.phrase} onChange={set('phrase')} /></div>
        <div><label className={label}>Sent on or after</label><input className={input} type="date" required value={f.since} onChange={set('since')} /></div>
        <div><label className={label}>Sent before</label><input className={input} type="date" required value={f.until} onChange={set('until')} /></div>
      </div>
      <ErrorNote>{error}</ErrorNote>
      <button className={btnPrimary} disabled={busy}>{busy ? 'Opening Google…' : 'Sign in at Google and fetch'}</button>
    </form>
  );
};

const ImportTab = ({ api, adminEmail, athleteName }) => {
  const [files, setFiles] = useState(null);
  const [batches, setBatches] = useState([]);
  const [pv, setPv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    api('/imports/files').then(setFiles).catch((e) => setError(e.message));
    api('/imports/batches').then(setBatches).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (name, fn) => { setBusy(name); setError(''); try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(''); } };
  const preview = (file_name, m = {}, d = {}) => run('preview', async () => {
    setDone(null);
    setPv(await api('/imports/preview', { method: 'POST', body: { file_name, mapping: m, college_decisions: d } }));
    setMapping(m); setDecisions(d);
  });
  const remap = (sheet, field, header) => {
    const current = { ...(mapping[sheet.name] || sheet.mapping) };
    if (header) current[field] = header; else delete current[field];
    preview(pv.file_name, { ...mapping, [sheet.name]: current }, decisions);
  };
  const decide = (key, value) => preview(pv.file_name, mapping, { ...decisions, [key]: value });
  const commit = () => run('commit', async () => {
    const s = pv.summary;
    if (!window.confirm(`Import ${pv.file_name}?\n\n${s.messages_confirmed_sent} sent, ${s.messages_sending_unconfirmed} unconfirmed, ${s.contacts_new} new contacts.\n${s.unresolved} unresolved rows will be left out.\n\nNothing is emailed. The import can be undone.`)) return;
    const r = await api('/imports/commit', { method: 'POST', body: { file_name: pv.file_name, file_sha256: pv.file_sha256, mapping, college_decisions: decisions } });
    setDone(r); setPv(null); load();
  });
  const undo = (b) => run('undo', async () => {
    const reason = window.prompt(`Undo the import of ${b.file_name}?\n\nIts imported messages are removed, along with contacts it created that have not been used since.\nWhy? (this is recorded)`);
    if (!reason) return;
    await api(`/imports/batches/${b.id}/undo`, { method: 'POST', body: { reason } });
    load();
  });

  const tile = (n, t, cls = 'text-white') => (
    <div className="rounded-lg bg-[#0A1628] border border-[#546E7A]/30 px-3 py-2"><div className={`text-xl font-bold ${cls}`}>{n}</div><div className="text-[11px] text-[#B0BEC5]">{t}</div></div>
  );
  const reasons = (o) => Object.entries(o || {}).map(([k, v]) => `${v} × ${k}`).join(' · ');
  const shown = pv ? pv.rows.filter((r) => filter === 'all' || r.category === filter) : [];

  return (
    <div className="space-y-5">
      <div className={`${card} text-sm text-[#B0BEC5]`}>
        <div className="text-white font-semibold mb-1">Import past outreach</div>
        Brings email that was already sent, and the coaches it went to, in from a spreadsheet as a record.
        Nothing is emailed by an import, and imported messages can never be approved or sent. You see exactly what
        will happen before anything is saved, and an import can be undone.
      </div>
      <ErrorNote>{error}</ErrorNote>
      {!pv && <SentMailFetch defaultMailbox={adminEmail} defaultPhrase={athleteName} />}

      {done && (
        <div className={`${card} border-[#00E676]/40 text-sm`}>
          <div className="text-[#00E676] font-semibold">Import #{done.batch_id} complete</div>
          <div className="text-[#B0BEC5] mt-1">
            {done.summary.messages_confirmed_sent} sent · {done.summary.messages_sending_unconfirmed} sending not confirmed ·{' '}
            {done.summary.contacts_new} new contacts · {done.summary.contacts_matched} matched · {done.summary.colleges_new} new colleges ·{' '}
            {done.summary.skipped} skipped · {done.summary.unresolved} unresolved
          </div>
        </div>
      )}

      {!pv && (
        <div className={card}>
          <div className="text-white font-semibold mb-2">Spreadsheets on the server</div>
          {!files ? <div className="text-[#546E7A] text-sm">Loading…</div>
            : !files.folder_available ? <div className="text-[#FFB300] text-sm">The import folder is not available on this server.</div>
              : files.files.length === 0 ? <div className="text-[#B0BEC5] text-sm">No spreadsheets are in the import folder yet.</div>
                : (
                  <div className="divide-y divide-[#546E7A]/20">
                    {files.files.map((f) => (
                      <div key={f.name} className="py-2.5 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="min-w-0 flex-1 text-sm">
                          <div className="text-white break-all">{f.name}</div>
                          <div className="text-[#546E7A] text-xs">{Math.max(1, Math.round(f.size / 1024))} KB · copied {fmtDateTime(f.modified)}
                            {f.already_imported_batch ? <span className="text-[#00E676]"> · already imported (import #{f.already_imported_batch})</span> : ''}</div>
                        </div>
                        {f.kind === 'outlook'
                          ? <span className="text-[#546E7A] text-xs">Outlook Sent Items export</span>
                          : <button className={btnPrimary} disabled={!!busy} onClick={() => preview(f.name)}><Upload size={14} /> {busy === 'preview' ? 'Reading…' : 'Preview'}</button>}
                      </div>
                    ))}
                  </div>
                )}
        </div>
      )}

      {pv && (
        <>
          <div className={`${card} border-[#00D4FF]/40`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div><div className="text-white font-semibold break-all">Preview: {pv.file_name}</div>
                <div className="text-[#546E7A] text-xs">Nothing has been saved. {pv.summary.rows} rows read.</div></div>
              <button className={btnGhost} onClick={() => setPv(null)}>Close preview</button>
            </div>
            {pv.already_imported && <ErrorNote>This exact file was already imported on {fmtDateTime(pv.already_imported.imported_at)} (import #{pv.already_imported.batch_id}). It cannot be imported again unless that import is undone.</ErrorNote>}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
              {tile(pv.summary.messages_confirmed_sent, 'Sent', 'text-[#B39DDB]')}
              {tile(pv.summary.messages_sending_unconfirmed, 'Sending not confirmed')}
              {tile(pv.summary.contact_only_rows, 'Contact-only rows', 'text-[#00D4FF]')}
              {tile(pv.summary.skipped, 'Skipped', 'text-[#FFB300]')}
              {tile(pv.summary.unresolved, 'Unresolved', 'text-[#EF5350]')}
            </div>
            <div className="text-[#B0BEC5] text-sm mt-3">
              Contacts: {pv.summary.contacts_new} new, {pv.summary.contacts_matched} already in the directory (only their blank fields are filled).
              Colleges: {pv.summary.colleges_new} new, {pv.summary.colleges_matched} matched.
            </div>
            {pv.summary.skipped > 0 && <div className="text-[#FFB300] text-xs mt-1">Skipped: {reasons(pv.summary.skipped_reasons)}</div>}
            {pv.summary.unresolved > 0 && <div className="text-[#EF5350] text-xs mt-1">Unresolved (left out): {reasons(pv.summary.unresolved_reasons)}</div>}
          </div>

          {pv.questions.length > 0 && (
            <div className={`${card} border-[#FFB300]/50`}>
              <div className="text-[#FFB300] font-semibold text-sm mb-2">Which college is this? Rows for these stay out until you answer.</div>
              {pv.questions.map((q) => (
                <div key={q.key} className="py-2 text-sm">
                  <div className="text-white">“{q.name}”</div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {q.candidates.map((c) => (
                      <button key={c.id || c.key} className={btnGhost} disabled={!!busy} onClick={() => decide(q.key, c.id || c.key)}>Same as “{c.name}”</button>
                    ))}
                    <button className={btnGhost} disabled={!!busy} onClick={() => decide(q.key, 'new')}>A different college</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pv.sheets.map((sh) => (
            <details key={sh.name} className={card}>
              <summary className="text-white text-sm font-semibold cursor-pointer">Columns in “{sh.name}” ({sh.rows} rows) — check they were read correctly</summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {pv.fields.map((f) => (
                  <label key={f} className="flex items-center gap-2 text-xs text-[#B0BEC5]">
                    <span className="w-24 flex-shrink-0">{FIELD_LABEL[f] || f}</span>
                    <select className={`${input} py-1.5`} value={sh.mapping[f] || ''} disabled={!!busy} onChange={(x) => remap(sh, f, x.target.value)}>
                      <option value="">(not in this sheet)</option>
                      {sh.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {sh.unmapped.length > 0 && <div className="text-[#546E7A] text-xs mt-3">Columns with no matching field are kept with each record as-is: {sh.unmapped.join(', ')}</div>}
            </details>
          ))}

          <div className={card}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-white text-sm font-semibold">Rows</span>
              {['all', ...Object.keys(CATEGORY)].map((k) => (
                <button key={k} onClick={() => setFilter(k)} className={`text-xs rounded px-2 py-1 border ${filter === k ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-[#546E7A]/40 text-[#B0BEC5]'}`}>
                  {k === 'all' ? 'All' : CATEGORY[k].text}
                </button>
              ))}
            </div>
            <div className="divide-y divide-[#546E7A]/20">
              {shown.map((r) => (
                <div key={r.ref} className="py-2 text-sm">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className={`text-xs font-bold ${CATEGORY[r.category].cls}`}>{CATEGORY[r.category].text.toUpperCase()}</span>
                    <span className="text-white">{r.college || <span className="text-[#EF5350]">no college</span>}</span>
                    <span className="text-[#B0BEC5]">{r.coach_name || 'coach name unknown'}</span>
                    <span className="text-[#546E7A] break-all">{r.email || 'no email'}</span>
                  </div>
                  <div className="text-[#546E7A] text-xs">
                    {r.ref}
                    {r.reason ? <span className="text-[#FFB300]"> · {r.reason}</span> : ''}
                    {r.contact_action ? ` · contact ${r.contact_action === 'new' ? 'will be added' : 'already in directory'}` : ''}
                    {['confirmed', 'unconfirmed'].includes(r.category) ? ` · sent: ${r.sent_at ? fmtImported(r.sent_at, r.sent_precision) : 'unknown'}${r.sent_original ? ` (spreadsheet: “${r.sent_original}”)` : ''} · subject: ${r.subject || 'unknown'} · message: ${r.has_body ? 'present' : 'unknown'}` : ''}
                    {r.notes.map((n) => <span key={n} className="text-[#FFB300]"> · {n}</span>)}
                  </div>
                </div>
              ))}
              {shown.length === 0 && <div className="text-[#546E7A] text-sm py-2">No rows of this kind.</div>}
            </div>
            {pv.rows_shown < pv.summary.rows && <div className="text-[#546E7A] text-xs mt-2">Showing the first {pv.rows_shown} of {pv.summary.rows} rows. The totals above cover all of them.</div>}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button className={btnPrimary} onClick={commit}
              disabled={!!busy || !!pv.already_imported || (pv.summary.rows - pv.summary.unresolved) === 0}>
              {busy === 'commit' ? 'Importing…' : 'Import these records'}
            </button>
            <span className="text-[#546E7A] text-xs">Unresolved rows are left out. No email is sent.</span>
          </div>
        </>
      )}

      {batches.length > 0 && (
        <div className={card}>
          <div className="text-white font-semibold mb-2">Imports so far</div>
          <div className="divide-y divide-[#546E7A]/20">
            {batches.map((b) => (
              <div key={b.id} className="py-2.5 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-white break-all">#{b.id} · {b.file_name}{b.status === 'undone' && <span className="text-[#EF5350] text-xs font-bold"> · UNDONE</span>}</div>
                  <div className="text-[#546E7A] text-xs">
                    Imported {fmtDateTime(b.imported_at)} by {b.imported_by || 'unknown'}{b.summary.carried_out_by ? ` (${b.summary.carried_out_by})` : ''} · {b.summary.messages_confirmed_sent} sent, {b.summary.messages_sending_unconfirmed} unconfirmed, {b.summary.contacts_new} new contacts{b.summary.college_only_rows ? `, ${b.summary.college_only_rows} prospect colleges` : ''}, {b.summary.skipped} skipped, {b.summary.unresolved} unresolved
                    {b.status === 'undone' ? ` · undone ${fmtDateTime(b.undone_at)} by ${b.undone_by || 'unknown'}: “${b.undo_reason}”` : ''}
                  </div>
                </div>
                {b.status === 'committed' && <button className={btnGhost} disabled={!!busy} onClick={() => undo(b)}><Undo2 size={14} /> Undo</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Shell ───────────────────────────────────────────────────────────────── */

const TABS = [
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'compose', label: 'Compose', Icon: Pencil },
  { id: 'inbox', label: 'Inbox', Icon: InboxIcon },
  { id: 'sent', label: 'Sent', Icon: Send },
  { id: 'drafts', label: 'Drafts', Icon: FileText },
  { id: 'campaign', label: 'Campaign', Icon: Megaphone },
  { id: 'questionnaires', label: 'Questionnaires', Icon: ClipboardCheck },
  { id: 'outreach', label: 'Outreach', Icon: ListChecks },
  { id: 'contacts', label: 'Contacts', Icon: Users },
  { id: 'discover', label: 'Discover', Icon: Compass },
  { id: 'settings', label: 'Settings', Icon: SettingsIcon },
  { id: 'activity', label: 'Activity', Icon: History },
];

const RecruitingCenter = ({ standalone = false }) => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [athletes, setAthletes] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [emailId, setEmailId] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(null);
  const [notice, setNotice] = useState('');
  const [talk, setTalk] = useState(null);          // the address whose conversation is open
  const [menu, setMenu] = useState(false);
  const [newReplies, setNewReplies] = useState(0);
  const api = useApi(playerId, isAdmin);

  // Coming back from Google's sign-in page.
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const result = query.get('gmail');
    if (!result) return;
    if (result.startsWith('history_')) {
      const why = {
        history_fetched: `Fetched ${query.get('n')} sent message(s) from the mailbox. The access has been withdrawn. They are ready to be matched against the imported records.`,
        history_wrong_account: 'That was a different Google account from the mailbox you named. Nothing was read. Try again and sign in as that mailbox.',
        history_read_permission_not_granted: 'Google did not grant permission to read. Nothing was read. Try again and leave the permission checked.',
      }[result] || `The sent mail could not be fetched (${result.replace('history_', '')}). Nothing was changed.`;
      setNotice(why);
      setTab('import');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const messages = {
      connected: 'The recruiting mailbox is connected.',
      wrong_account: 'That was a different Google account. Sign in as the recruiting mailbox itself and try again.',
      send_permission_not_granted: 'Google did not grant permission to send. Try again and leave the permission checked.',
      declined: 'The connection was canceled at Google.',
      expired: 'That sign-in link had expired. Start the connection again.',
    };
    setNotice(messages[result] || `The mailbox could not be connected (${result}).`);
    setTab('settings');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);
  useEffect(() => { if (playerId) api('/me').then((d) => setSending(d.sending)).catch(() => {}); }, [api, playerId, tab]);
  // Look for new mail when the Recruiting Center is opened and when moving between tabs.
  useEffect(() => {
    if (!playerId) return;
    (async () => {
      try {
        let box = await api('/inbox');
        if (box.state.enabled) {
          const r = await api('/inbox/sync', { method: 'POST' });
          if (r.new) box = await api('/inbox');
        }
        setNewReplies(box.new_replies);
      } catch { /* the Inbox tab says what is wrong */ }
    })();
  }, [api, playerId, tab, talk, emailId]);

  useEffect(() => {
    authFetch(`${API}/api/recruiting/athletes`).then(async (r) => {
      if (!r.ok) throw new Error('You do not have access to a Recruiting Center.');
      const list = await r.json();
      setAthletes(list);
      if (list.length === 1) setPlayerId(list[0].player_id);
    }).catch((e) => setError(e.message));
  }, []);

  const athlete = athletes?.find((a) => a.player_id === playerId);
  const go = (t) => { setEmailId(null); setTalk(null); setTab(t); window.scrollTo(0, 0); };
  const openEmail = (id) => { setTalk(null); setEmailId(id); window.scrollTo(0, 0); };
  const openTalk = (address) => { setEmailId(null); setTalk(address); window.scrollTo(0, 0); };

  return (
    <div className={standalone ? 'min-h-screen bg-[#0A0E27]' : ''}>
      <div className="max-w-5xl mx-auto px-4 py-5 sm:py-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white">Recruiting Center</h1>
            {athlete && (
              <div className="text-[#B0BEC5] text-sm">
                <span className="text-white font-semibold">{athlete.name}</span>
                {/* His standard identification, exactly as it appears under every message. */}
                {(athlete.identification || '').split('\n').map((x) => x.trim()).filter((x) => x && x !== athlete.name)
                  .map((x) => <span key={x}> · {x}</span>)}
                {isAdmin && <div className="text-[#546E7A] text-xs mt-0.5">You are signed in as administrator.</div>}
              </div>
            )}
          </div>
          {standalone && (
            <button onClick={() => { logout(); window.location.replace('/recruiting-login'); }} className={`${btnGhost} whitespace-nowrap flex-shrink-0`}><LogOut size={14} /> Sign out</button>
          )}
        </div>

        <ErrorNote>{error}</ErrorNote>

        {athletes && !playerId && (
          athletes.length === 0 ? <div className={`${card} text-sm text-[#B0BEC5]`}>No athlete has a Recruiting Center yet.</div> : (
            <div className={card}>
              <div className="text-white font-semibold mb-2">Whose Recruiting Center?</div>
              <div className="flex flex-wrap gap-2">
                {athletes.map((a) => <button key={a.player_id} className={btnGhost} onClick={() => setPlayerId(a.player_id)}>{a.name}</button>)}
              </div>
            </div>
          )
        )}

        {playerId && (
          <>
            {notice && <div className="rounded-lg border border-[#00D4FF]/40 bg-[#00D4FF]/10 px-3 py-2 text-sm text-white mb-3">{notice}</div>}
            {sending && (
              <div className="rounded-lg border border-[#546E7A]/40 bg-[#243B55]/50 px-3 py-2 text-sm text-[#B0BEC5] mb-4 flex items-start gap-2">
                {sending.connected && sending.coach_sending_enabled ? (
                  <><Send size={15} className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    <span><b className="text-white">Nothing is sent automatically.</b> An email goes out only when you press Send and confirm, one message at a time.</span></>
                ) : !sending.connected ? (
                  <><Ban size={15} className="text-[#FFB300] mt-0.5 flex-shrink-0" />
                    <span><b className="text-white">Email is not connected.</b> You can write messages here, but nothing can be emailed yet.</span></>
                ) : (
                  <><Ban size={15} className="text-[#FFB300] mt-0.5 flex-shrink-0" />
                    <span><b className="text-white">Sending to coaches is turned off.</b> You can write messages here, but nothing is emailed to a coach yet.</span></>
                )}
              </div>
            )}
            {(() => {
              const all = [...TABS.filter((t) => isAdmin || !['settings', 'activity'].includes(t.id)), ...(isAdmin ? [{ id: 'import', label: 'Import', Icon: Upload }] : [])];
              const here = all.find((t) => t.id === tab) || all[0];
              const badge = (id) => (id === 'inbox' && newReplies > 0
                ? <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#00E676] text-[#0A0E27] text-[10px] font-bold px-1">{newReplies}</span> : null);
              return (
                <>
                  {/* Phones: one menu button that opens every section. */}
                  <div className="md:hidden mb-4">
                    <button onClick={() => setMenu(!menu)} aria-expanded={menu} aria-label="Recruiting Center menu"
                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#546E7A]/40 bg-[#1A2744] px-3 py-3 text-sm font-semibold text-white">
                      <span className="flex items-center gap-2 min-w-0"><here.Icon size={16} className="text-[#00D4FF] flex-shrink-0" /> <span className="truncate">{here.label}</span> {badge(here.id) || (here.id !== 'inbox' && newReplies > 0 && <span className="text-[#00E676] text-xs font-normal whitespace-nowrap">{newReplies} new in Inbox</span>)}</span>
                      <span className="flex items-center gap-1.5 text-[#B0BEC5] flex-shrink-0">Menu {menu ? <CloseIcon size={18} /> : <MenuIcon size={18} />}</span>
                    </button>
                    {menu && (
                      <div className="mt-1 rounded-lg border border-[#546E7A]/40 bg-[#1A2744] p-2 grid grid-cols-2 gap-1">
                        {all.map(({ id, label: text, Icon }) => (
                          <button key={id} onClick={() => { setMenu(false); go(id); }}
                            className={`flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-semibold text-left ${tab === id ? 'bg-[#00D4FF]/15 text-[#00D4FF]' : 'text-[#B0BEC5]'}`}>
                            <Icon size={16} className="flex-shrink-0" /> {text} {badge(id)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Wider screens: every section in view, wrapping onto a second line if it must. */}
                  <div className="hidden md:flex flex-wrap gap-1 border-b border-[#546E7A]/30 pb-2 mb-5">
                    {all.map(({ id, label: text, Icon }) => (
                      <button key={id} onClick={() => go(id)}
                        className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold whitespace-nowrap ${tab === id ? 'bg-[#00D4FF]/15 text-[#00D4FF]' : 'text-[#B0BEC5] hover:bg-[#243B55]/50'}`}>
                        <Icon size={15} /> {text} {badge(id)}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
            {talk && <Conversation api={api} address={talk} back={() => setTalk(null)} openEmail={openEmail} />}
            {!talk && !emailId && tab === 'dashboard' && <Dashboard api={api} go={go} openEmail={openEmail} />}
            {!talk && !emailId && tab === 'outreach' && <Outreach api={api} openTalk={openTalk} />}
            {!talk && !emailId && tab === 'inbox' && <InboxTab api={api} openTalk={openTalk} isAdmin={isAdmin} />}
            {!talk && emailId && <EmailEditor key={emailId} api={api} id={emailId} back={() => setEmailId(null)} isAdmin={isAdmin} />}
            {!talk && !emailId && tab === 'compose' && <Compose api={api} openEmail={openEmail} go={go} />}
            {!talk && !emailId && tab === 'sent' && <SentList api={api} openEmail={openEmail} go={go} />}
            {!talk && !emailId && tab === 'drafts' && <Messages api={api} openEmail={openEmail} go={go} view="drafts" />}
            {!talk && !emailId && tab === 'questionnaires' && <Questionnaires api={api} openEmail={openEmail} />}
            {!talk && !emailId && tab === 'campaign' && <Campaign api={api} openEmail={openEmail} go={go} />}
            {!talk && !emailId && tab === 'discover' && <Discover api={api} go={go} isAdmin={isAdmin} />}
            {!talk && !emailId && tab === 'contacts' && <Contacts api={api} openEmail={openEmail} isAdmin={isAdmin} />}
            {!talk && !emailId && tab === 'settings' && isAdmin && <SettingsTab api={api} isAdmin={isAdmin} openEmail={openEmail} />}
            {!talk && !emailId && tab === 'activity' && isAdmin && <Activity api={api} isAdmin={isAdmin} />}
            {!talk && !emailId && tab === 'import' && isAdmin && <ImportTab api={api} adminEmail={user?.email} athleteName={athlete?.name} />}
          </>
        )}
      </div>
    </div>
  );
};

export default RecruitingCenter;
